/* eslint-env browser */
// Bookings (approved board W2_Portal_Tables): one table built for an
// eight-hour shift. A filter bar (search, time range, status, type, bulk
// convert), a legend that says what the marks mean, and a table with a
// sticky header, zebra rows, right-aligned money, each fare marked Fixed
// price / Estimate / "est." (owner rule), an Anomaly column instead of row
// colours, a Comfortable / Compact switch and a Columns menu. Clicking a row
// opens Booking details. No new reads: every row comes from the bookings
// already in Redux; the time range control is the same explicit range fetch
// as before.
//
// Call-centre feedback (29 Oct 2026): the reference and the customer's phone
// each have a copy button (the phone is copied without spaces, ready for
// MicroSIP); the reference can also be selected by hand, and a click that
// ends a text selection never opens the booking. The search box has an X to
// clear it. "Hide filters" folds the time range, status and type controls
// away (kept per person); the filters still apply and show as chips.
//
// The status chip, fare tag and fare analysis below are exported so Booking
// details draws the same words, icons and colours.
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  LinearProgress,
  Link,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { CopyButton, SearchField, useFiltersOpen, FilterToggleButton, ActiveFilters } from "../components/modern";
import { formatPhoneDisplay as formatPhoneText } from "../components/modern/tableUtils";
import { useTheme } from "@mui/material/styles";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import DirectionsCarFilledRoundedIcon from "@mui/icons-material/DirectionsCarFilledRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import RouteRoundedIcon from "@mui/icons-material/RouteRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import DensitySmallRoundedIcon from "@mui/icons-material/DensitySmallRounded";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { api } from "common";
import { getLangKey } from "common/src/other/getLangKey";
import ConfirmationDialogRaw from "../components/ConfirmationDialogRaw";
import UsersCombo from "../components/UsersCombo";
import ClickableUserLink from "../components/ClickableUserLink";
import ClickableBookingLink from "../components/ClickableBookingLink";
import { selectingTextIn, pressPoint } from "../common/textSelection";
import {
  BidModal,
  acceptBid,
  showEst,
  getBookingType,
  getBookingTypeLabel,
  bookingTypeLabelFor,
  BOOKING_TYPE_ORDER,
} from "../common/sharedFunctions";
import { tokens as c, bookingStatusTone, tones } from "../theme";
import { showToast, confirmDialog, EmptyState, TableSkeletonRows } from "../components/feedback";
import { exportCsv, printTable, datedFileName, formatAddisTime } from "../common/exportUtils";
import { usePageState, onPageStateReset } from "../components/modern/pageState";

// The time range this browser tab loaded last, and for whom (a range fetch
// replaces the list in the store, which outlives this page): coming back
// from a booking keeps the chosen range without loading it again.
let loadedWindow = null; // { uid, days }
onPageStateReset(() => { loadedWindow = null; });

const icons = {
  paypal: require("../assets/payment-icons/paypal-logo.png").default,
  braintree: require("../assets/payment-icons/braintree-logo.png").default,
  stripe: require("../assets/payment-icons/stripe-logo.png").default,
  paytm: require("../assets/payment-icons/paytm-logo.png").default,
  payulatam: require("../assets/payment-icons/payulatam-logo.png").default,
  flutterwave: require("../assets/payment-icons/flutterwave-logo.png").default,
  paystack: require("../assets/payment-icons/paystack-logo.png").default,
  securepay: require("../assets/payment-icons/securepay-logo.png").default,
  payfast: require("../assets/payment-icons/payfast-logo.png").default,
  liqpay: require("../assets/payment-icons/liqpay-logo.png").default,
  culqi: require("../assets/payment-icons/culqi-logo.png").default,
  mercadopago: require("../assets/payment-icons/mercadopago-logo.png").default,
  squareup: require("../assets/payment-icons/squareup-logo.png").default,
  wipay: require("../assets/payment-icons/wipay-logo.png").default,
  test: require("../assets/payment-icons/test-logo.png").default,
  razorpay: require("../assets/payment-icons/razorpay-logo.png").default,
  paymongo:require("../assets/payment-icons/paymongo-logo.png").default,
  iyzico:require("../assets/payment-icons/iyzico-logo.png").default,
  tap:require("../assets/payment-icons/tap-logo.png").default,
  chapa:require("../assets/payment-icons/chapa-logo.png").default,
  telebirr:require("../assets/payment-icons/telebirr-logo.png").default,
  santimpay:require('../assets/payment-icons/santimpay-logo.png').default
};

// ── Shared booking helpers (also used by BookingDetails) ────────────────────

const ADDIS_OFFSET_MS = 3 * 60 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Booking times arrive as epoch ms (number or digit string) or ISO text.
export const toMs = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const s = String(value).trim();
  if (/^\d{9,}$/.test(s)) return Number(s);
  const ms = new Date(s).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// "25 Oct" for this year, "25 Oct 2025" otherwise, on the Addis Ababa calendar.
export const addisShortDate = (value) => {
  const ms = toMs(value);
  if (ms === null) return "";
  const a = new Date(ms + ADDIS_OFFSET_MS);
  const now = new Date(Date.now() + ADDIS_OFFSET_MS);
  const year = a.getUTCFullYear() !== now.getUTCFullYear() ? ` ${a.getUTCFullYear()}` : "";
  return `${a.getUTCDate()} ${MONTHS[a.getUTCMonth()]}${year}`;
};

// Live status words are stored in capitals ("PAYMENT PENDING", Oromo
// "HAARAA"). Show them in sentence case; Amharic, Tigrinya and words that
// already have small letters stay as they are.
export const calmCaps = (text) => {
  const s = String(text === null || text === undefined ? "" : text);
  if (!/[A-Z]/.test(s) || /[a-z]/.test(s)) return s;
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const bookingStatusLabel = (status, t) => {
  if (!status) return "";
  return calmCaps(t(status, { defaultValue: String(status).replace(/_/g, " ") }));
};

const STATUS_ICON = {
  NEW: AccessTimeRoundedIcon,
  PENDING: AccessTimeRoundedIcon,
  PAYMENT_PENDING: AccessTimeRoundedIcon,
  PENDING_DRIVER_APPROVAL: HourglassEmptyRoundedIcon,
  ACCEPTED: DirectionsCarFilledRoundedIcon,
  ARRIVED: PlaceRoundedIcon,
  STARTED: RouteRoundedIcon,
  REACHED: FlagRoundedIcon,
  PAID: CheckRoundedIcon,
  COMPLETE: CheckRoundedIcon,
  CANCELLED: CloseRoundedIcon,
};

// Status as an icon and a word, dark on a pale tint (Q15).
export function BookingStatusChip({ status, size = "small", sx }) {
  const { t } = useTranslation();
  const tone = bookingStatusTone(status);
  const Icon = STATUS_ICON[status] || InfoOutlinedIcon;
  return (
    <Chip
      size={size}
      icon={<Icon />}
      label={bookingStatusLabel(status, t)}
      sx={{
        color: tone.fg,
        bgcolor: tone.bg,
        fontWeight: 600,
        height: size === "small" ? 26 : 30,
        maxWidth: "100%",
        "& .MuiChip-icon": { color: tone.fg, fontSize: 16, ml: "8px" },
        "& .MuiChip-label": { px: 1, whiteSpace: "nowrap" },
        ...(sx || {}),
      }}
    />
  );
}

// Owner rule (fixed-price-vs-estimate): a fare the customer changed before
// the request went out (offerFare > 0) is a "Fixed price" everywhere; a fare
// nobody changed is an "Estimate" until the trip ends; after the trip the
// final fare shows with the system estimate under it ("est. 280").
// Mirrors mobile-app/src/common/fareInfo.js isFixedFare.
export const isFixedFare = (b) =>
  !!b && parseFloat(b.offerFare) > 0 && !b.deliveryWithBid && !b.selectedBid && b.manual_cost_adjustment !== true;

const FINISHED_STATUSES = ["REACHED", "PENDING", "PAID", "COMPLETE"];

export const fareInfoOf = (b) => {
  const cost = parseFloat(b && b.trip_cost);
  const est = parseFloat(b && b.estimate);
  const amount = Number.isFinite(cost) && cost > 0 ? cost : Number.isFinite(est) && est > 0 ? est : 0;
  const kind = isFixedFare(b) ? "fixed" : b && FINISHED_STATUSES.indexOf(b.status) >= 0 ? "final" : "estimate";
  return { kind, amount, estimate: Number.isFinite(est) && est > 0 ? est : null };
};

// 1240 -> "1,240" (tabular digits come from the cell style).
export const formatAmount = (value, decimals) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: Number.isInteger(decimals) ? decimals : 2 });
};

// "420 ETB" or "ETB 420", the way settings.swipe_symbol asks.
export const formatMoney = (value, settings) => {
  const num = formatAmount(value, settings && settings.decimal);
  if (num === "") return "";
  const sym = (settings && settings.symbol) || "";
  if (!sym) return num;
  return settings && settings.swipe_symbol ? `${num} ${sym}` : `${sym} ${num}`;
};

const ESTIMATE_BORDER = "#D97706";

// The small mark under (or beside) a fare: a blue lock with "Fixed price",
// or the dashed amber "≈ Estimate" chip. Nothing for a final fare.
export function FareTag({ kind, sx }) {
  const { t } = useTranslation();
  if (kind === "fixed") {
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: c.INFO_TEXT, fontSize: 12.5, fontWeight: 600, lineHeight: 1.45, whiteSpace: "nowrap", ...(sx || {}) }}
      >
        <LockRoundedIcon sx={{ fontSize: 14 }} aria-hidden="true" />
        {t("fixed_price", { defaultValue: "Fixed price" })}
      </Box>
    );
  }
  if (kind === "estimate") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 0.75,
          border: `1.5px dashed ${ESTIMATE_BORDER}`,
          borderRadius: "6px",
          bgcolor: c.ESTIMATE_TINT,
          color: c.AMBER_TEXT,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.6,
          whiteSpace: "nowrap",
          ...(sx || {}),
        }}
      >
        {`≈ ${t("estimated_fare", { defaultValue: "Estimate" })}`}
      </Box>
    );
  }
  return null;
}

const round1 = (n) => (Math.round(n * 10) / 10).toFixed(1);

// Price, time and distance against the system estimate, with the same
// suspicion score the portal used before (now in one place). Time factors
// count only once the trip has a real duration.
export const analyseFare = (b) => {
  const tripCost = parseFloat(b && b.trip_cost) || 0;
  const estimate = parseFloat(b && b.estimate) || 0;
  const estTime = parseFloat(b && b.estimateTime) || 0;
  const totalTime = parseFloat(b && b.total_trip_time) || 0;
  const estDist = parseFloat(b && b.estimateDistance) || 0;
  const actDist = parseFloat(b && b.distance) || 0;
  const pickupAdd = b && (b.pickupAddress || (b.pickup && b.pickup.add));
  const dropAdd = b && (b.dropAddress || (b.drop && b.drop.add));
  const validTrip = pickupAdd !== dropAdd;
  const hasPrice = estimate > 0;
  const hasTime = estTime > 0 && totalTime > 0;
  const hasDist = validTrip && estDist > 0;
  const price = hasPrice ? tripCost / estimate : 1;
  const time = hasTime ? totalTime / estTime : 1;
  const dist = estDist > 0 ? actDist / estDist : 1;

  let score = 0;
  const factors = [];
  const add = (points, key) => {
    score += points;
    factors.push(key);
  };
  if (price >= 3) add(40, "extreme_price");
  else if (price >= 2) add(30, "high_price");
  else if (price >= 1.5) add(20, "moderate_price");
  else if (price >= 1.3) add(10, "slight_price");
  if (hasTime) {
    if (time < 0.8 && price > 1.3) add(25, "shorter_higher");
    else if (time > 1.5 && price > 1.5) add(-10, "longer_justifies");
    else if (time < 1.2 && price > 2) add(20, "normal_time_excessive");
  }
  if (hasDist) {
    if (dist > 1.5) add(25, "excessive_distance");
    else if (dist > 1.3) add(15, "longer_route");
    if (dist > 1.3 && price > 1.5) add(10, "long_route_high_price");
    else if (dist < 1.1 && price > 1.5) add(15, "normal_distance_high_price");
    if (dist > 1.2 && Math.abs(price - dist) < 0.2) add(-10, "price_matches_distance");
  }
  if (hasDist && hasTime) {
    const avg = (price + time + dist) / 3;
    const variance = Math.abs(price - avg) + Math.abs(time - avg) + Math.abs(dist - avg);
    if (variance > 2) add(10, "inconsistent");
    else if (variance < 0.5) add(-5, "consistent");
  } else if (hasTime) {
    const gap = Math.abs(price - time);
    if (gap > 1.5) add(10, "price_time_mismatch");
    else if (gap < 0.3) add(-5, "proportional");
  }
  score = Math.max(0, Math.min(100, score));

  // The marks shown in the Anomaly column (same thresholds as the old count).
  const marks = [];
  if (hasPrice && price > 1.5) marks.push({ kind: "price_above", value: price });
  else if (hasPrice && price < 0.7) marks.push({ kind: "price_below", value: price });
  if (hasTime && time > 1.5) marks.push({ kind: "longer", value: time });
  else if (hasTime && time < 0.5) marks.push({ kind: "quicker", value: time });

  return {
    tripCost,
    estimate,
    estTime,
    totalTime,
    estDist,
    actDist,
    hasPrice,
    hasTime,
    hasDist,
    price,
    time,
    dist,
    score,
    factors,
    marks,
    underpriced: hasPrice && price < 0.7,
    offlineId: b && b.offline_metadata && b.offline_metadata.offline_id ? b.offline_metadata.offline_id : null,
  };
};

const FACTOR_TEXT = {
  extreme_price: "Extreme price increase",
  high_price: "High price increase",
  moderate_price: "Moderate price increase",
  slight_price: "Slight price increase",
  shorter_higher: "Shorter trip, higher cost",
  longer_justifies: "Longer trip explains the cost",
  normal_time_excessive: "Normal time, very high cost",
  excessive_distance: "Much longer distance than planned",
  longer_route: "Longer route taken",
  long_route_high_price: "Long route and high price",
  normal_distance_high_price: "Normal distance, high price",
  price_matches_distance: "Price matches the distance",
  inconsistent: "Price, time and distance disagree",
  consistent: "Price, time and distance agree",
  price_time_mismatch: "Price and time disagree",
  proportional: "Price follows the time",
};

// The analysis as short lines of text (tooltip in the table, visible text on
// Booking details).
export const analysisLines = (a, t, settings) => {
  const lines = [];
  const unit = settings && settings.convert_to_mile ? t("mile", { defaultValue: "mi" }) : t("km", { defaultValue: "km" });
  if (a.offlineId) lines.push(t("anomaly_line_offline", { id: a.offlineId, defaultValue: "Offline meter trip {{id}}" }));
  if (a.hasPrice) {
    lines.push(t("anomaly_line_price", { value: round1(a.price), amount: formatMoney(a.estimate, settings), defaultValue: "Price: {{value}}× estimate ({{amount}})" }));
  }
  if (a.hasTime) {
    lines.push(
      t("anomaly_line_time", {
        value: round1(a.time),
        from: round1(a.estTime / 60),
        to: round1(a.totalTime / 60),
        defaultValue: "Time: {{value}}× estimate ({{from}} → {{to}} min)",
      })
    );
  }
  if (a.hasDist) {
    lines.push(
      t("anomaly_line_distance", {
        value: round1(a.dist),
        from: round1(a.estDist),
        to: round1(a.actDist),
        unit,
        defaultValue: "Distance: {{value}}× estimate ({{from}} → {{to}} {{unit}})",
      })
    );
  }
  if (a.underpriced) {
    lines.push(
      t("anomaly_line_underpriced", {
        percent: Math.round(a.price * 100),
        defaultValue: "Well below the estimate ({{percent}}% of it). The driver may have given a discount.",
      })
    );
  } else if (a.score >= 30) {
    lines.push(t("anomaly_line_score", { percent: Math.round(a.score), defaultValue: "Confidence: {{percent}}% suspicious" }));
    if (a.factors.length) {
      const list = a.factors.map((k) => t(`anomaly_factor_${k}`, { defaultValue: FACTOR_TEXT[k] })).join(", ");
      lines.push(t("anomaly_line_factors", { list, defaultValue: "Factors: {{list}}" }));
    }
  }
  return lines;
};

const MARK_ICON = {
  price_above: TrendingUpRoundedIcon,
  price_below: TrendingDownRoundedIcon,
  longer: TimerOutlinedIcon,
  quicker: BoltRoundedIcon,
};

export const anomalyMarkText = (mark, t) => {
  const value = round1(mark.value);
  if (mark.kind === "longer") return t("bookings_anomaly_chip_longer", { value, defaultValue: "{{value}}× longer" });
  if (mark.kind === "quicker") return t("bookings_anomaly_chip_quicker", { value, defaultValue: "{{value}}× time" });
  return t("bookings_anomaly_chip_price", { value, defaultValue: "{{value}}× estimate" });
};

// One anomaly mark: icon + words on the warning tint (red tint when the
// suspicion score is critical).
export function AnomalyMark({ mark, critical, label }) {
  const tone = critical ? tones.error : tones.warning;
  const Icon = MARK_ICON[mark.kind] || TrendingUpRoundedIcon;
  return (
    <Chip
      size="small"
      icon={<Icon />}
      label={label}
      sx={{
        color: tone.fg,
        bgcolor: tone.bg,
        fontWeight: 600,
        height: 26,
        "& .MuiChip-icon": { color: tone.fg, fontSize: 16, ml: "8px" },
        "& .MuiChip-label": { px: 1, whiteSpace: "nowrap" },
      }}
    />
  );
}

// No spaces (owner, 29 Oct 2026): "+251920439275", never the US-style "+251 92 043 9275" - in the portal every number reads as stored and pastes straight into MicroSIP (see components/modern/tableUtils.formatPhoneDisplay).
export const formatPhoneDisplay = (value) => formatPhoneText(String(value || "").trim());

// ── Bookings page ───────────────────────────────────────────────────────────

// Bulk-conversion option lists. Payment modes mirror the per-row editor.
// Both conversions only ever touch PENDING bookings — the same
// "PENDING-only" rule the per-row editor enforces via canEditBooking — so
// the original status is fixed, not user-selectable. Status "to" options
// mirror the per-row status editor's targets.
const BULK_FROM_STATUS = 'PENDING';
const BULK_PAYMENT_MODES = ['cash', 'card', 'wallet'];
const BULK_STATUS_TO = ['REACHED', 'COMPLETE'];
const ROW_STATUS_TARGETS = ['CANCELLED', 'REACHED', 'COMPLETE'];

const DEFAULT_STATUS_FILTER = {
  NEW: true,
  ACCEPTED: true,
  ARRIVED: true,
  STARTED: true,
  REACHED: true,
  PENDING: true,
  PAYMENT_PENDING: true,
  COMPLETE: true,  // Show by default to include offline bookings
  CANCELLED: false  // Hidden by default
};

// Columns: time, ref and the action slots always show; the rest can be
// hidden from the Columns menu (four are hidden by default, as drawn).
const HIDEABLE_COLUMNS = ['id', 'customer', 'driver', 'status', 'bookingType', 'otp', 'payment_mode', 'fare', 'anomaly'];
const DEFAULT_HIDDEN_COLUMNS = ['id', 'bookingType', 'otp', 'payment_mode'];
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const ZEBRA = '#FAFAFB';

// Per-person table preferences (density, rows per page, hidden columns) in
// this browser only; the page works without them.
const prefKey = (name, uid) => `dd_bookings_${name}_${uid || 'anon'}`;
const readPref = (name, uid, fallback) => {
  try {
    const raw = window.localStorage.getItem(prefKey(name, uid));
    return raw === null ? fallback : JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};
const writePref = (name, uid, value) => {
  try {
    window.localStorage.setItem(prefKey(name, uid), JSON.stringify(value));
  } catch (e) {
    // private window or blocked storage: keep the choice for this visit only
  }
};

const digitsOf = (s) => String(s || '').replace(/\D/g, '');

// A click on one of these inside a row belongs to it, not to the row (the
// same list as the modern DataTable).
const ROW_INTERACTIVE =
  'a,button,input,select,textarea,label,[role="button"],[role="switch"],[role="checkbox"],[role="combobox"],[role="option"],[role="menuitem"],[role="link"],[data-no-row-click]';

const BookingHistory = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { cancelBooking, updateBooking, RequestPushMsg, forceEndBooking, fetchBookings } = api;
  const dispatch = useDispatch();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const userdata = useSelector((state) => state.usersdata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const role = useSelector((state) => state.auth.profile.usertype);
  const uid = auth && auth.profile ? auth.profile.uid : null;
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const providers = useSelector((state) => state.paymentmethods.providers);
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [data, setData] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const [users, setUsers] = useState(null);
  const [userCombo, setUserCombo] = useState(null);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [assignBooking, setAssignBooking] = useState(null);
  const [bidModalStatus, setBidModalStatus] = useState(true);
  const [selectedBidder, setSelectedBidder] = useState();
  const [walletModalStatus, setWalletModalStatus] = useState(false);
  const {state} = useLocation();
  // The filters, search, sort, time range and page are kept while staff open
  // a booking and come back (owner, 28 Oct: never reset a list's filters on
  // the way back from a detail page); components/modern/pageState.js.
  const [currentPage, setCurrentPage] = usePageState('bookings', 'page', 0);

  // Row editing (status / payment mode), from the More menu.
  const [moreMenu, setMoreMenu] = useState({ anchorEl: null, row: null });
  const [editDialog, setEditDialog] = useState({ open: false, kind: null, row: null, value: '' });

  // State for status filter
  const [statusKept, setStatusFilter] = usePageState('bookings', 'status', DEFAULT_STATUS_FILTER);
  // A status added later starts shown (a kept filter never hides it).
  const statusFilter = useMemo(() => ({ ...DEFAULT_STATUS_FILTER, ...statusKept }), [statusKept]);
  // Booking-type filter — parallel to statusFilter, keyed by the derived type
  // (app, call_center, admin, street_pickup, donkey_meter, offline). All on by
  // default so the table is unchanged until the user narrows it.
  const [bookingTypeKept, setBookingTypeFilter] = usePageState(
    'bookings', 'types', () => BOOKING_TYPE_ORDER.reduce((acc, key) => { acc[key] = true; return acc; }, {})
  );
  const bookingTypeFilter = useMemo(
    () => ({ ...BOOKING_TYPE_ORDER.reduce((acc, key) => { acc[key] = true; return acc; }, {}), ...bookingTypeKept }),
    [bookingTypeKept]
  );
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = usePageState('bookings', 'search', '');
  // "Hide filters" folds the time range, status and type controls away (each
  // person's choice, kept in this browser); they still apply and show as chips.
  const [filtersOpen, toggleFilters] = useFiltersOpen('bookings');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [sort, setSort] = usePageState('bookings', 'sort', { by: 'time', dir: 'desc' });
  const [density, setDensity] = useState(() => (readPref('density', uid, 'comfortable') === 'compact' ? 'compact' : 'comfortable'));
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = Number(readPref('rpp', uid, 25));
    return ROWS_PER_PAGE_OPTIONS.indexOf(saved) >= 0 ? saved : 25;
  });
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    const saved = readPref('hidden_cols', uid, DEFAULT_HIDDEN_COLUMNS);
    return Array.isArray(saved) ? saved.filter((id) => HIDEABLE_COLUMNS.indexOf(id) >= 0) : DEFAULT_HIDDEN_COLUMNS;
  });

  // ── Bulk conversion (admin / call-center supervisor only) ──────────
  // Two independent processes — payment-mode and booking-status — each
  // picking an ORIGINAL value to match and a NEW value to apply across
  // the rows currently loaded in the table (filteredData). Kept separate
  // on purpose: payment mode and status are never converted together.
  const canBulkConvert = role === 'admin' || role === 'callcentersupervisor';
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPmFrom, setBulkPmFrom] = useState('cash');
  const [bulkPmTo, setBulkPmTo] = useState('wallet');
  const [bulkStatusTo, setBulkStatusTo] = useState('COMPLETE');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  // Default look-back shown in the "Time range" control. Mirrors the role-based
  // default fetchBookings uses at login (admin/finance 30d; call-center 7d) so
  // the control reflects what's already loaded. Changing it loads older/newer
  // on demand (an explicit range fetch) — so call-center agents keep access to
  // older bookings without paying for them on every login.
  const defaultWindowDays = (role === 'admin' || role === 'finance') ? 30 : 7;
  const [bookingWindowDays, setBookingWindowDays] = usePageState('bookings', 'window', defaultWindowDays);

  const loadWindow = (days) => {
    loadedWindow = { uid, days };
    const now = new Date();
    now.setDate(now.getDate() + 1);
    now.setHours(23, 59, 59, 999);
    const from = new Date();
    from.setDate(from.getDate() - days);
    from.setHours(0, 0, 0, 0);
    dispatch(fetchBookings({ fromDate: from.toISOString(), toDate: now.toISOString() }));
  };
  const handleBookingWindowChange = (days) => {
    setBookingWindowDays(days);
    setCurrentPage(0);
    loadWindow(days);
  };
  // A kept range other than the sign-in default is loaded once per tab (after
  // a reload the store holds the default range again); coming back from a
  // booking it is already in the store, so nothing is read.
  useEffect(() => {
    const loaded = loadedWindow && loadedWindow.uid === uid && loadedWindow.days === bookingWindowDays;
    if (bookingWindowDays !== defaultWindowDays && !loaded) loadWindow(bookingWindowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(()=>{
    if (state && Number.isInteger(state.pageNo)) setCurrentPage(state.pageNo);
  },[state])

  // Local display overrides for rows the admin just converted (id -> {status,
  // payment_mode}). updateBooking's reducer is a no-op (cache + 24h tail-
  // listener architecture), so without this a Redux refresh — e.g. a new
  // booking streaming in — would re-sync `data` from Redux and visually revert
  // a converted row. Re-applied on every sync below; pure client state, zero
  // RTDB cost.
  const [bookingOverrides, setBookingOverrides] = useState({});

  useEffect(() => {
    const src = bookinglistdata.bookings || [];
    const ids = Object.keys(bookingOverrides);
    if (ids.length === 0) {
      setData(src);
      return;
    }
    // Apply each override on top of Redux. Drop only the ones Redux has already
    // caught up to (so we never permanently mask a later legitimate change);
    // keep overrides whose booking is momentarily absent from this refresh.
    const stillNeeded = { ...bookingOverrides };
    const merged = src.map((b) => {
      const ov = bookingOverrides[b.id];
      if (!ov) return b;
      const reconciled = Object.keys(ov).every((k) => b[k] === ov[k]);
      if (reconciled) { delete stillNeeded[b.id]; return b; }
      return { ...b, ...ov };
    });
    setData(merged);
    if (Object.keys(stillNeeded).length !== ids.length) {
      setBookingOverrides(stillNeeded); // prune reconciled — converges next pass
    }
  }, [bookinglistdata.bookings, bookingOverrides]);

  // Record a display override for a converted booking so it survives Redux
  // re-syncs. Only the table-visible fields need overriding.
  const rememberBookingOverride = (updated) => {
    setBookingOverrides((prev) => ({
      ...prev,
      [updated.id]: { status: updated.status, payment_mode: updated.payment_mode },
    }));
  };

  // Filter data based on status filter + booking-type filter
  useEffect(() => {
    if (data && data.length > 0) {
      const filtered = data.filter(booking => {
        return statusFilter[booking.status] === true && bookingTypeFilter[getBookingType(booking)] === true;
      });
      setFilteredData(filtered);
    } else {
      setFilteredData([]);
    }
  }, [data, statusFilter, bookingTypeFilter]);

  useEffect(() => {
    if (userdata.users) {
      // While the approval gate is on (settings.approvalGateEnabled) a driver
      // who is not approved cannot accept, so the assign list leaves them out.
      const gateOn = settings && settings.approvalGateEnabled === true;
      let arr = [];
      for (let i = 0; i < userdata.users.length; i++) {
        let user = userdata.users[i];
        if (
          user.usertype === "driver" &&
          ((user.fleetadmin === auth.profile.uid && role === "fleetadmin") ||
            role === "admin") &&
          (!gateOn || user.approved === true)
        ) {
          arr.push({
            firstName: user.firstName,
            lastName: user.lastName,
            mobile: user.mobile,
            email: user.email,
            uid: user.id,
            desc:
              user.firstName +
              " " +
              user.lastName +
              " (" +
              (settings.AllowCriticalEditsAdmin
                ? user.mobile
                : t('hidden_demo', { defaultValue: 'Hidden' })) +
              ") " +
              (settings.AllowCriticalEditsAdmin
                ? user.email
                : t('hidden_demo', { defaultValue: 'Hidden' })),
            pushToken: user.pushToken ? user.pushToken : "",
            carType: user.carType,
          });
        }
      }
      setUsers(arr);
    }
  }, [
    userdata.users,
    settings.AllowCriticalEditsAdmin,
    settings.approvalGateEnabled,
    auth.profile.uid,
    role,
    t,
  ]);

  const paymentModeLabel = (mode) => {
    const m = mode || 'cash';
    return calmCaps(t(m, { defaultValue: m.charAt(0).toUpperCase() + m.slice(1) }));
  };

  const assignDriver = () => {
    if (!assignBooking || !userCombo) return;
    const booking = {
      ...assignBooking,
      requestedDrivers: { ...(assignBooking.requestedDrivers || {}), [userCombo.uid]: true },
    };
    dispatch(updateBooking(booking));
    RequestPushMsg(userCombo.pushToken, {
      title: t("notification_title"),
      msg: t("new_booking_notification"),
      screen: "DriverTrips"
    });
    setUserCombo(null);
    handleClose();
    showToast.success(t("driver_assigned_notified", { defaultValue: "Driver assigned and notified." }));
  };

  const onConfirmClose = (value) => {
    if (value) {
      dispatch(
        cancelBooking({
          reason: value,
          booking: selectedBooking,
          cancelledBy: role,
        })
      );
      showToast.success(t("bookings_cancelled_toast", { reference: (selectedBooking && selectedBooking.reference) || '', defaultValue: "Booking {{reference}} cancelled." }));
    }
    setOpenConfirm(false);
  };

  const handleChange = (e) => {
    if (e.target.name === "selectedProviderIndex") {
      setSelectedProviderIndex(parseInt(e.target.value));
      setSelectedProvider(providers[parseInt(e.target.value)]);
    }
    if (e.target.name === "selectedBidder") {
      setSelectedBidder(e.target.value);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePaymentModalClose = (e) => {
    setTimeout(() => {
      setPaymentModalStatus(false);
    }, 1500);
  };

  const handleWalletModalClose = (e) => {
    setTimeout(() => {
      setWalletModalStatus(false);
    }, 1500);
  };

  useEffect(() => {
    if (providers) {
      setSelectedProvider(providers[0]);
    }
  }, [providers]);

  const processPayment = (rowData) => {
    const curBooking = { ...rowData };
    if (curBooking.payment_mode === "card") {
      let paymentCost = curBooking.selectedBid
        ? curBooking.selectedBid.trip_cost
        : curBooking.trip_cost;
      const paymentPacket = {
        payment_mode: "card",
        customer_paid: parseFloat(paymentCost).toFixed(settings.decimal),
        cardPaymentAmount: parseFloat(paymentCost).toFixed(settings.decimal),
        discount: 0,
        usedWalletMoney: 0,
        cashPaymentAmount: 0,
        promo_applied: false,
        promo_details: null,
        payableAmount: parseFloat(paymentCost).toFixed(settings.decimal),
      };
      curBooking.paymentPacket = paymentPacket;
      dispatch(updateBooking(curBooking));
      setSelectedBooking(curBooking);
      setPaymentModalStatus(true);
    }
    if (curBooking.payment_mode === "wallet") {
      let curBooking = { ...rowData };
      if (rowData.status === "PAYMENT_PENDING") {
        curBooking.status = "NEW";
        curBooking.prepaid = true;
      } else if (rowData.status === "PENDING") {
        curBooking.status = "PAID";
      } else if (rowData.status === "NEW") {
        curBooking.prepaid = true;
        curBooking.status = "ACCEPTED";
      }

      curBooking.customer_paid =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount =
        curBooking.status === "ACCEPTED"
          ? parseFloat(curBooking.selectedBid.trip_cost).toFixed(
              settings.decimal
            )
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);

      if (curBooking.status === "ACCEPTED") {
        curBooking.driver = curBooking.selectedBid.driver;
        curBooking.driver_image = curBooking.selectedBid.driver_image;
        curBooking.driver_name = curBooking.selectedBid.driver_name;
        curBooking.driver_contact = curBooking.selectedBid.driver_contact;
        curBooking.driver_token = curBooking.selectedBid.driver_token;
        curBooking.vehicle_number = curBooking.selectedBid.vehicle_number;
        curBooking.vehicleModel = curBooking.selectedBid.vehicleModel;
        curBooking.vehicleMake = curBooking.selectedBid.vehicleMake;
        curBooking.driverRating = curBooking.selectedBid.driverRating;
        curBooking.trip_cost = curBooking.selectedBid.trip_cost;
        curBooking.convenience_fees = curBooking.selectedBid.convenience_fees;
        curBooking.driver_share = curBooking.selectedBid.driver_share;
        curBooking.fleetadmin = curBooking.selectedBid.fleetadmin ? curBooking.selectedBid.fleetadmin : null;
        curBooking.fleetCommission= curBooking.selectedBid.fleetadmin ? ((parseFloat( curBooking.selectedBid.trip_cost) - parseFloat(curBooking.selectedBid.convenience_fees)) * (parseFloat(curBooking.fleet_admin_comission) / 100)).toFixed(2) : null;
        curBooking.driverOffers = {};
        curBooking.requestedDrivers = {};
        curBooking.driverEstimates = {};
        curBooking.selectedBid = {};
      }
      dispatch(updateBooking(curBooking));

      setBidModalStatus(false);
    }
    if (curBooking.payment_mode === "cash") {
      let curBooking = { ...rowData };
      if (rowData.status === "PAYMENT_PENDING") {
        curBooking.status = "NEW";
      } else if (rowData.status === "PENDING") {
        curBooking.status = "PAID";
      } else if (rowData.status === "NEW") {
        curBooking.status = "ACCEPTED";
      }

      curBooking.customer_paid =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney = 0;
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount =
        curBooking.status === "ACCEPTED"
          ? 0
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.payableAmount =
        curBooking.status === "ACCEPTED"
          ? parseFloat(curBooking.selectedBid.trip_cost).toFixed(
              settings.decimal
            )
          : parseFloat(curBooking.trip_cost).toFixed(settings.decimal);

      if (curBooking.status === "ACCEPTED") {
        curBooking.driver = curBooking.selectedBid.driver;
        curBooking.driver_image = curBooking.selectedBid.driver_image;
        curBooking.driver_name = curBooking.selectedBid.driver_name;
        curBooking.driver_contact = curBooking.selectedBid.driver_contact;
        curBooking.driver_token = curBooking.selectedBid.driver_token;
        curBooking.vehicle_number = curBooking.selectedBid.vehicle_number;
        curBooking.vehicleModel = curBooking.selectedBid.vehicleModel;
        curBooking.vehicleMake = curBooking.selectedBid.vehicleMake;
        curBooking.driverRating = curBooking.selectedBid.driverRating;
        curBooking.trip_cost = curBooking.selectedBid.trip_cost;
        curBooking.convenience_fees = curBooking.selectedBid.convenience_fees;
        curBooking.driver_share = curBooking.selectedBid.driver_share;
        curBooking.fleetadmin = curBooking.selectedBid.fleetadmin ? curBooking.selectedBid.fleetadmin : null;
        curBooking.fleetCommission= curBooking.selectedBid.fleetadmin ? ((parseFloat( curBooking.selectedBid.trip_cost) - parseFloat(curBooking.selectedBid.convenience_fees)) * parseFloat(curBooking.fleet_admin_comission) / 100).toFixed(2):null;
        curBooking.driverOffers = {};
        curBooking.requestedDrivers = {};
        curBooking.driverEstimates = {};
        curBooking.selectedBid = {};
      }
      dispatch(updateBooking(curBooking));
      setBidModalStatus(false);
    }
  };

  const handleWalletPayment = (e) => {
    e.preventDefault();
    let curBooking = { ...selectedBooking };
    let paymentCost = curBooking.selectedBid
      ? curBooking.selectedBid.trip_cost
      : curBooking.trip_cost;
    if (parseFloat(paymentCost) > parseFloat(auth.profile.walletBalance)) {
      showToast.error(t('wallet_balance_low', { defaultValue: 'Your wallet balance is too low.' }));
    } else {
      curBooking.prepaid =
        curBooking.status === "NEW" || curBooking.status === "PAYMENT_PENDING"
          ? true
          : false;
      curBooking.status = curBooking.status === "NEW" ? "ACCEPTED" : "PAID";
      curBooking.customer_paid = parseFloat(paymentCost).toFixed(
        settings.decimal
      );
      curBooking.discount = 0;
      curBooking.usedWalletMoney = parseFloat(paymentCost).toFixed(
        settings.decimal
      );
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount = 0;
      curBooking.promo_applied = false;
      curBooking.promo_details = null;
      curBooking.paymentPacket = null;
      curBooking.driver = curBooking.selectedBid
        ? curBooking.selectedBid.driver
        : curBooking.driver;
      curBooking.driver_image = curBooking.selectedBid
        ? curBooking.selectedBid.driver_image
        : curBooking.driver_image;
      curBooking.driver_name = curBooking.selectedBid
        ? curBooking.selectedBid.driver_name
        : curBooking.driver_name;
      curBooking.driver_contact = curBooking.selectedBid
        ? curBooking.selectedBid.driver_contact
        : curBooking.driver_contact;
      curBooking.driver_token = curBooking.selectedBid
        ? curBooking.selectedBid.driver_token
        : curBooking.driver_token;
      curBooking.vehicle_number = curBooking.selectedBid
        ? curBooking.selectedBid.vehicle_number
        : curBooking.vehicle_number;
      curBooking.vehicleModel = curBooking.selectedBid
        ? curBooking.selectedBid.vehicleModel
        : curBooking.vehicleModel;
      curBooking.vehicleMake = curBooking.selectedBid
        ? curBooking.selectedBid.vehicleModel
        : curBooking.vehicleModel;
      curBooking.driverRating = curBooking.selectedBid
        ? curBooking.selectedBid.driverRating
        : curBooking.driverRating;
      curBooking.trip_cost = curBooking.selectedBid
        ? curBooking.selectedBid.trip_cost
        : curBooking.trip_cost;
      curBooking.convenience_fees = curBooking.selectedBid
        ? curBooking.selectedBid.convenience_fees
        : curBooking.convenience_fees;
      curBooking.driver_share = curBooking.selectedBid
        ? curBooking.selectedBid.driver_share
        : curBooking.driver_share;
      curBooking.driverOffers = null;
      curBooking.requestedDrivers = null;
      curBooking.driverEstimates = null;
      curBooking.selectedBid = null;

      dispatch(updateBooking(curBooking));

      setTimeout(() => {
        setWalletModalStatus(false);
      }, 1500);
    }
  };

  const handleBidModalClose = () => {
    setBidModalStatus(false);
  };

  // Function to check if user can edit booking. Single source of truth for
  // both the per-row editors and the bulk conversions, so they enforce the
  // exact same rule (role + PENDING-only). Memoized so it can sit in the
  // bulk-match useMemo dependency lists without recomputing every render.
  const canEditBooking = useCallback((booking) => {
    const allowedRoles = ['admin', 'callcentersupervisor'];
    const editableStatuses = ['PENDING'];

    return allowedRoles.includes(role) &&
           editableStatuses.includes(booking.status) &&
           !['COMPLETE', 'CANCELLED', 'REACHED'].includes(booking.status);
  }, [role]);

  // Pure transform for a booking-status conversion. Returns the updated
  // booking, applying the same payment recomputation the per-row editor
  // uses (COMPLETE/REACHED settle the payment fields and remap to
  // PAID/PENDING; any other target just sets the raw status). Shared by
  // the per-row editor and the bulk action so both behave identically.
  const computeStatusUpdate = (booking, newStatus) => {
    const updatedBooking = { ...booking };
    const payment_mode = booking.payment_mode || 'cash';

    // Apply payment logic based on payment mode when completing booking
    if (newStatus === 'COMPLETE' || newStatus === 'REACHED') {

      // CASH PAYMENT LOGIC
      if (payment_mode === 'cash') {
        updatedBooking.status = newStatus === 'COMPLETE' ? 'PAID' : 'PENDING';
        updatedBooking.payment_mode = 'cash';
        updatedBooking.customer_paid = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
        updatedBooking.discount = parseFloat(booking.discount || 0).toFixed(settings.decimal);
        updatedBooking.usedWalletMoney = 0;
        updatedBooking.cardPaymentAmount = 0;
        updatedBooking.cashPaymentAmount = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
        updatedBooking.payableAmount = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
      }

      // WALLET PAYMENT LOGIC
      else if (payment_mode === 'wallet') {
        updatedBooking.status = newStatus === 'COMPLETE' ? 'PAID' : 'PENDING';
        updatedBooking.payment_mode = 'wallet';
        updatedBooking.prepaid = true;
        updatedBooking.customer_paid = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
        updatedBooking.discount = parseFloat(booking.discount || 0).toFixed(settings.decimal);
        updatedBooking.usedWalletMoney = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
        updatedBooking.cardPaymentAmount = 0;
        updatedBooking.cashPaymentAmount = 0;
        updatedBooking.payableAmount = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
      }

      // CARD PAYMENT LOGIC
      else if (payment_mode === 'card') {
        updatedBooking.status = newStatus === 'COMPLETE' ? 'PAID' : 'PENDING';
        updatedBooking.payment_mode = 'card';
        updatedBooking.prepaid = true;
        updatedBooking.customer_paid = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
        updatedBooking.discount = parseFloat(booking.discount || 0).toFixed(settings.decimal);
        updatedBooking.usedWalletMoney = 0;
        updatedBooking.cardPaymentAmount = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
        updatedBooking.cashPaymentAmount = 0;
        updatedBooking.payableAmount = parseFloat(booking.trip_cost || 0).toFixed(settings.decimal);
      }

    } else {
      // For other status changes, just update status
      updatedBooking.status = newStatus;
    }

    return updatedBooking;
  };

  // Pure transform for a payment-mode conversion. Mirrors the per-row editor.
  const computePaymentModeUpdate = (booking, newPaymentMode) => ({
    ...booking,
    payment_mode: newPaymentMode,
    prepaid: newPaymentMode !== 'cash'
  });

  // Patch a single booking row in the table by id. updateBooking's reducer is a
  // no-op (cache + live-tail-listener architecture), so without this the edited
  // row keeps showing its old value until reload. Recorded as an override so it
  // also survives a later Redux re-sync (e.g. a new booking streaming in).
  const patchLocalBooking = (updated) => {
    rememberBookingOverride(updated);
  };

  // Function to handle status change
  const handleStatusChange = (booking, newStatus) => {
    if (!canEditBooking(booking)) return;

    const updated = computeStatusUpdate(booking, newStatus);
    dispatch(updateBooking(updated));
    patchLocalBooking(updated);
    showToast.success(
      t("bookings_status_changed", {
        reference: booking.reference || '',
        status: bookingStatusLabel(newStatus, t),
        defaultValue: "Status of {{reference}} changed to {{status}}.",
      })
    );
  };

  // Function to handle payment mode change
  const handlePaymentModeChange = (booking, newPaymentMode) => {
    if (!canEditBooking(booking)) return;

    const updated = computePaymentModeUpdate(booking, newPaymentMode);
    dispatch(updateBooking(updated));
    patchLocalBooking(updated);
    showToast.success(
      t("bookings_payment_changed", {
        reference: booking.reference || '',
        mode: paymentModeLabel(newPaymentMode),
        defaultValue: "Payment mode of {{reference}} changed to {{mode}}.",
      })
    );
  };

  // Loaded rows the bulk conversions would touch. Gated by the exact same
  // canEditBooking rule the per-row editors use (role + PENDING-only), then
  // narrowed by the selected payment mode for the payment conversion. The
  // status conversion only touches CASH bookings — card/wallet ones settle
  // through their payment provider and must never be bulk-completed.
  // Drives the live count badges and the run loops.
  const bulkPaymentMatches = useMemo(
    () => (canBulkConvert ? filteredData.filter(b => canEditBooking(b) && (b.payment_mode || 'cash') === bulkPmFrom) : []),
    [canBulkConvert, filteredData, bulkPmFrom, canEditBooking]
  );
  const bulkStatusMatches = useMemo(
    () => (canBulkConvert ? filteredData.filter(b => canEditBooking(b) && (b.payment_mode || 'cash') === 'cash') : []),
    [canBulkConvert, filteredData, canEditBooking]
  );

  // Run whichever conversion was confirmed. Iterates a snapshot of the
  // matched rows (captured here, before dispatches re-filter the table)
  // and applies the SAME transform the per-row editor uses to each one.
  const runBulkConvert = async (kind) => {
    setBulkProcessing(true);
    let done = 0;
    // Collect what we wrote, keyed by id, so we can patch the table rows
    // locally afterwards (see below).
    const updatedById = {};

    try {
      if (kind === 'payment') {
        const rows = bulkPaymentMatches;
        setBulkProgress({ done: 0, total: rows.length });
        for (const b of rows) {
          const updated = computePaymentModeUpdate(b, bulkPmTo);
          await dispatch(updateBooking(updated));
          updatedById[updated.id] = updated;
          done++;
          setBulkProgress({ done, total: rows.length });
        }
        showToast.success(
          t("bulk_pm_done", {
            count: done,
            from: paymentModeLabel(bulkPmFrom),
            to: paymentModeLabel(bulkPmTo),
            defaultValue: "Payment mode of {{count}} bookings changed from {{from}} to {{to}}.",
          })
        );
      } else if (kind === 'status') {
        // Hard guard: status conversion is cash-only. bulkStatusMatches already
        // filters, but never let a non-cash booking through the write path.
        const rows = bulkStatusMatches.filter(b => (b.payment_mode || 'cash') === 'cash');
        setBulkProgress({ done: 0, total: rows.length });
        for (const b of rows) {
          const updated = computeStatusUpdate(b, bulkStatusTo);
          await dispatch(updateBooking(updated));
          updatedById[updated.id] = updated;
          done++;
          setBulkProgress({ done, total: rows.length });
        }
        showToast.success(
          t("bulk_status_done", {
            count: done,
            to: bookingStatusLabel(bulkStatusTo, t),
            defaultValue: "{{count}} pending cash bookings changed to {{to}}.",
          })
        );
      }

      // Reflect the persisted changes in the table immediately. updateBooking's
      // UPDATE_BOOKING reducer is a no-op — it patches the async bookings cache
      // and relies on the live tail listener, which only watches the last 24h —
      // so older rows wouldn't visibly refresh. Record an override per changed
      // row so the new status / payment mode shows right away AND survives a
      // later Redux re-sync, with no extra RTDB reads. (A real refetch later
      // reconciles any server-side rewrites, e.g. PAID → COMPLETE for web.)
      if (Object.keys(updatedById).length) {
        setBookingOverrides((prev) => {
          const next = { ...prev };
          Object.values(updatedById).forEach((u) => {
            next[u.id] = { status: u.status, payment_mode: u.payment_mode };
          });
          return next;
        });
      }
    } catch (err) {
      showToast.error(
        t("bulk_stopped", { count: done, error: err && err.message ? err.message : '', defaultValue: "Bulk conversion stopped after {{count}} bookings: {{error}}" })
      );
    } finally {
      setBulkProcessing(false);
    }
  };

  // Ask first (M11: a specific title and verb), then convert.
  const requestBulkPaymentConvert = async () => {
    if (bulkPmFrom === bulkPmTo) {
      showToast.warning(t("bulk_pick_different_mode", { defaultValue: "Pick a payment mode different from the original." }));
      return;
    }
    if (bulkPaymentMatches.length === 0) {
      showToast.info(t("bulk_no_pm_matches", { mode: paymentModeLabel(bulkPmFrom), defaultValue: "No pending bookings use {{mode}}." }));
      return;
    }
    const ok = await confirmDialog({
      title: t("confirm_bulk_convert", { count: bulkPaymentMatches.length, defaultValue: "Convert {{count}} bookings?" }),
      body: t("bulk_confirm_payment_body", {
        count: bulkPaymentMatches.length,
        from: paymentModeLabel(bulkPmFrom),
        to: paymentModeLabel(bulkPmTo),
        defaultValue: "The payment mode of {{count}} loaded bookings changes from {{from}} to {{to}}. Each booking is updated on its own, so a large batch takes a moment.",
      }),
      confirmLabel: t("convert", { defaultValue: "Convert" }),
    });
    if (ok) runBulkConvert('payment');
  };

  // Original status is always PENDING (per-row "PENDING-only" rule).
  const requestBulkStatusConvert = async () => {
    if (bulkStatusMatches.length === 0) {
      showToast.info(t("bulk_no_status_matches", { defaultValue: "No pending cash bookings are loaded." }));
      return;
    }
    const ok = await confirmDialog({
      title: t("confirm_bulk_convert", { count: bulkStatusMatches.length, defaultValue: "Convert {{count}} bookings?" }),
      body: t("bulk_confirm_status_body", {
        count: bulkStatusMatches.length,
        to: bookingStatusLabel(bulkStatusTo, t),
        defaultValue: "{{count}} pending cash bookings change to {{to}}. Each booking is updated on its own, so a large batch takes a moment.",
      }),
      confirmLabel: t("convert", { defaultValue: "Convert" }),
    });
    if (ok) runBulkConvert('status');
  };

  // ── Row actions ──────────────────────────────────────────────────────
  const openDetails = (row) => {
    navigate(`/bookings/bookingdetails/${row.id}`, { state: { pageNo: currentPage } });
  };

  // A row opens its booking, except for a click that belongs to a button,
  // link or menu in it, lands in a menu or tooltip opened from it, or ends a
  // text selection (staff dragging across a reference or a phone number to
  // copy it). Where the press started tells a drag from a plain click.
  const pressRef = useRef(null);
  const notePress = (event) => {
    pressRef.current = pressPoint(event);
  };
  const handleRowClick = (event, row) => {
    const rowEl = event.currentTarget;
    const target = event.target;
    if (!target || !rowEl.contains(target)) return;
    const hit = typeof target.closest === 'function' ? target.closest(ROW_INTERACTIVE) : null;
    if (hit && hit !== rowEl && rowEl.contains(hit)) return;
    if (selectingTextIn(rowEl, event, pressRef.current)) return;
    openDetails(row);
  };

  // Donkey Meter bookings never pass through NEW/ACCEPTED —
  // they go PENDING_DRIVER_APPROVAL → STARTED → PAID. The
  // call center must be able to force-cancel a stuck join
  // request or a running meter (customer phoned support), so
  // those statuses are cancellable for meter rows too.
  const isCancellable = (row) =>
    row.status === "NEW" ||
    row.status === "ACCEPTED" ||
    (row.booking_type === "donkey_meter" &&
      (row.status === "PENDING_DRIVER_APPROVAL" || row.status === "STARTED"));

  const handleCancelClick = (row) => {
    if (
      settings.AllowCriticalEditsAdmin &&
      (role === "customer" ||
        role === "admin" ||
        role === "fleetadmin" ||
        role === "callcenteragent" ||
        role === "callcentersupervisor")
    ) {
      if (isCancellable(row)) {
        setSelectedBooking(row);
        setOpenConfirm(true);
      }
    } else {
      showToast.warning(t('demo_mode', { defaultValue: 'Not allowed in this mode.' }));
    }
  };

  const canForceEnd = role === "admin" || role === "callcentersupervisor";

  const handleForceEnd = async (row) => {
    const ok = await confirmDialog({
      title: t("bookings_force_end_title", { reference: row.reference || '', defaultValue: "Force end trip {{reference}}?" }),
      body: t("bookings_force_end_body", { defaultValue: "The trip ends now and the customer is told it is finished." }),
      confirmLabel: calmCaps(t("force_end", { defaultValue: "Force end" })),
      destructive: true,
    });
    if (!ok) return;
    dispatch(forceEndBooking({ ...row }));
    showToast.success(t("bookings_force_ended", { reference: row.reference || '', defaultValue: "Trip {{reference}} ended." }));
  };

  const canAssign = (row) =>
    row.status === "NEW" && (role === "admin" || role === "fleetadmin") && settings.autoDispatch === false && showEst;
  const canSelectBid = (row) => row.status === "NEW" && role === "customer" && !!row.driverOffers;
  const canPayNow = (row) => (row.status === "PAYMENT_PENDING" || row.status === "PENDING") && role === "customer";

  const closeMoreMenu = () => setMoreMenu({ anchorEl: null, row: null });

  const openEditDialog = (kind, row) => {
    closeMoreMenu();
    setEditDialog({
      open: true,
      kind,
      row,
      value: kind === 'status' ? '' : (row.payment_mode || 'cash'),
    });
  };

  const closeEditDialog = () => setEditDialog((prev) => ({ ...prev, open: false }));

  const saveEditDialog = () => {
    const { kind, row, value } = editDialog;
    if (!row || !value) return;
    if (kind === 'status') handleStatusChange(row, value);
    else if (kind === 'payment' && value !== (row.payment_mode || 'cash')) handlePaymentModeChange(row, value);
    closeEditDialog();
  };

  // ── Filters, search, sort, paging ────────────────────────────────────
  const handleStatusFilterChange = (status) => {
    setStatusFilter(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
    setCurrentPage(0);
  };

  const handleSelectAll = () => {
    const newFilter = {};
    Object.keys(statusFilter).forEach(key => {
      newFilter[key] = true;
    });
    setStatusFilter(newFilter);
    setCurrentPage(0);
  };

  const handleDeselectAll = () => {
    const newFilter = {};
    Object.keys(statusFilter).forEach(key => {
      newFilter[key] = false;
    });
    setStatusFilter(newFilter);
    setCurrentPage(0);
  };

  const handleBookingTypeFilterChange = (typeKey) => {
    setBookingTypeFilter(prev => ({ ...prev, [typeKey]: !prev[typeKey] }));
    setCurrentPage(0);
  };

  const handleSelectAllBookingTypes = () => {
    setBookingTypeFilter(BOOKING_TYPE_ORDER.reduce((acc, key) => { acc[key] = true; return acc; }, {}));
    setCurrentPage(0);
  };

  const handleDeselectAllBookingTypes = () => {
    setBookingTypeFilter(BOOKING_TYPE_ORDER.reduce((acc, key) => { acc[key] = false; return acc; }, {}));
    setCurrentPage(0);
  };

  const statusShown = Object.values(statusFilter).filter(Boolean).length;
  const statusTotal = Object.keys(statusFilter).length;
  const typeShown = BOOKING_TYPE_ORDER.filter((k) => bookingTypeFilter[k]).length;
  const statusFilterChanged = Object.keys(DEFAULT_STATUS_FILTER).some((k) => DEFAULT_STATUS_FILTER[k] !== statusFilter[k]);
  const typeFilterChanged = typeShown !== BOOKING_TYPE_ORDER.length;
  const activeFilterCount = (statusFilterChanged ? 1 : 0) + (typeFilterChanged ? 1 : 0) + (search.trim() ? 1 : 0);

  const clearFilters = () => {
    setStatusFilter(DEFAULT_STATUS_FILTER);
    handleSelectAllBookingTypes();
    setSearch('');
    setCurrentPage(0);
  };

  // While the filters are folded away, the ones that narrow the list stay in
  // view as chips; a chip's × puts that filter back to its default. (The time
  // range shows in the table header; the search box never folds away.)
  const hiddenFilterTokens = [
    statusFilterChanged
      ? {
          key: 'status',
          group: t('status', { defaultValue: 'Status' }),
          value: t('bookings_filter_count', { shown: statusShown, total: statusTotal, defaultValue: '{{shown}} of {{total}}' }),
        }
      : null,
    typeFilterChanged
      ? {
          key: 'type',
          group: t('type', { defaultValue: 'Type' }),
          value: t('bookings_filter_count', { shown: typeShown, total: BOOKING_TYPE_ORDER.length, defaultValue: '{{shown}} of {{total}}' }),
        }
      : null,
  ].filter(Boolean);

  const removeHiddenFilter = (key) => {
    if (key === 'status') {
      setStatusFilter(DEFAULT_STATUS_FILTER);
      setCurrentPage(0);
    } else if (key === 'type') {
      handleSelectAllBookingTypes();
    }
  };

  // Search: reference, booking id, names, car type and phone numbers. A phone
  // typed as 0911…, 911… or +251 911… finds the same booking.
  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return filteredData;
    const qDigits = digitsOf(q).replace(/^0+/, '');
    const phoneLike = qDigits.length >= 4 && /^[\d\s+()-]+$/.test(q);
    return filteredData.filter((b) => {
      const text = [b.reference, b.id, b.customer_name, b.driver_name, b.carType, b.vehicle_number, b.otp]
        .filter((v) => v !== null && v !== undefined && v !== false)
        .join(' ')
        .toLowerCase();
      if (text.indexOf(q) >= 0) return true;
      if (phoneLike) {
        return digitsOf(b.customer_contact).indexOf(qDigits) >= 0 || digitsOf(b.driver_contact).indexOf(qDigits) >= 0;
      }
      return false;
    });
  }, [filteredData, search]);

  const sortedRows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const valueOf = (b) => {
      switch (sort.by) {
        case 'reference': return String(b.reference || '');
        case 'customer': return String(b.customer_name || '');
        case 'driver': return String(b.driver_name || '');
        case 'status': return String(b.status || '');
        case 'fare': return fareInfoOf(b).amount;
        default: return toMs(b.bookingDate) || 0;
      }
    };
    return searchedRows.slice().sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [searchedRows, sort]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const page = Math.min(currentPage || 0, pageCount - 1);
  const pageRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (by) => {
    setSort((prev) => (prev.by === by ? { by, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { by, dir: by === 'time' || by === 'fare' ? 'desc' : 'asc' }));
    setCurrentPage(0);
  };

  const handleDensity = (event, value) => {
    if (!value) return;
    setDensity(value);
    writePref('density', uid, value);
  };

  const toggleColumn = (id) => {
    setHiddenColumns((prev) => {
      const next = prev.indexOf(id) >= 0 ? prev.filter((x) => x !== id) : prev.concat([id]);
      writePref('hidden_cols', uid, next);
      return next;
    });
  };

  // The payment mode column stays with the roles that had it before (admin
  // and call-centre supervisor, who can also change it); other roles never
  // get it in the Columns menu.
  const canSeePaymentMode = role === 'admin' || role === 'callcentersupervisor';
  const hideableColumns = canSeePaymentMode ? HIDEABLE_COLUMNS : HIDEABLE_COLUMNS.filter((id) => id !== 'payment_mode');
  const hiddenCount = hiddenColumns.filter((id) => hideableColumns.indexOf(id) >= 0).length;
  const isVisible = (id) => hideableColumns.indexOf(id) >= 0 && hiddenColumns.indexOf(id) < 0;

  // Anomaly counts for the legend (the old "anomaly summary"): bookings
  // with any mark, and how many carry each kind of mark.
  const anomalyCounts = useMemo(() => {
    const counts = { any: 0, price_above: 0, price_below: 0, longer: 0, quicker: 0 };
    filteredData.forEach((b) => {
      const marks = analyseFare(b).marks;
      if (marks.length) counts.any += 1;
      marks.forEach((m) => { counts[m.kind] += 1; });
    });
    return counts;
  }, [filteredData]);
  const anomalyCount = anomalyCounts.any;

  const loadingFirst = bookinglistdata.loading && (!data || data.length === 0);
  const refreshing = bookinglistdata.loading && data && data.length > 0;

  // ── Export (admin): CSV and print / PDF with Amharic intact (M15) ────
  const exportColumns = () => {
    const cols = [
      { title: t('booking_date', { defaultValue: 'Booking date' }), field: 'bookingDate', type: 'datetime' },
      { title: t('booking_id', { defaultValue: 'Booking ID' }), field: 'id' },
      { title: t('booking_ref', { defaultValue: 'Booking reference' }), field: 'reference' },
      { title: t('car_type', { defaultValue: 'Car type' }), value: (r) => (r.carType ? t(getLangKey(r.carType)) : '') },
      { title: t('driver_name', { defaultValue: 'Driver name' }), field: 'driver_name' },
      { title: t('customer_name', { defaultValue: 'Customer name' }), field: 'customer_name' },
      { title: t('customer_contact', { defaultValue: 'Customer phone' }), field: 'customer_contact' },
      { title: t('booking_status_web', { defaultValue: 'Booking status' }), value: (r) => bookingStatusLabel(r.status, t) },
      { title: t('booking_type_col', { defaultValue: 'Booking type' }), value: (r) => getBookingTypeLabel(r, t) },
      { title: t('bookings_col_start_code', { defaultValue: 'Start code' }), value: (r) => (r.otp ? r.otp : '') },
      { title: t('system_estimate', { defaultValue: 'System estimate' }), field: 'estimate', type: 'number', align: 'right' },
      { title: t('trip_cost', { defaultValue: 'Trip cost' }), field: 'trip_cost', type: 'number', align: 'right' },
      {
        title: t('bookings_col_fare_kind', { defaultValue: 'Fare type' }),
        value: (r) => {
          const kind = fareInfoOf(r).kind;
          if (kind === 'fixed') return t('fixed_price', { defaultValue: 'Fixed price' });
          if (kind === 'estimate') return t('estimated_fare', { defaultValue: 'Estimate' });
          return t('bookings_fare_final', { defaultValue: 'Final' });
        },
      },
    ];
    if (role === 'admin' || role === 'callcentersupervisor') {
      cols.push({ title: t('payment_mode_web', { defaultValue: 'Payment mode' }), value: (r) => paymentModeLabel(r.payment_mode) });
    }
    return cols;
  };

  const handleExportCsv = () => {
    setExportMenuAnchor(null);
    exportCsv({ columns: exportColumns(), rows: sortedRows, fileName: datedFileName('bookings', 'csv') });
  };

  const handleExportPdf = () => {
    setExportMenuAnchor(null);
    printTable({
      title: t('nav_bookings', { defaultValue: 'Bookings' }),
      subtitle: headerSummary,
      columns: exportColumns(),
      rows: sortedRows,
      fileName: datedFileName('bookings', ''),
      landscape: true,
    });
  };

  const isAdmin = auth.profile.usertype === "admin";
  const canExportCsv = isAdmin && !!settings.AllowCriticalEditsAdmin;

  // ── Text pieces ──────────────────────────────────────────────────────
  const headerCount = t('bookings_count', { count: sortedRows.length, defaultValue: '{{count}} bookings', defaultValue_one: '{{count}} booking' });
  const headerWindow = t('bookings_window', { count: bookingWindowDays, defaultValue: 'last {{count}} days' });
  const headerSummary = [
    headerCount,
    headerWindow,
    statusFilter.CANCELLED ? null : t('bookings_cancelled_hidden', { defaultValue: 'cancelled hidden' }),
  ].filter(Boolean).join(' · ');

  const windowLabel = (days) => {
    if (days === 7) return t('last_7_days', { defaultValue: 'Last 7 days' });
    if (days === 30) return t('last_30_days', { defaultValue: 'Last 30 days' });
    return t('last_90_days', { defaultValue: 'Last 90 days' });
  };

  const nextWindow = bookingWindowDays < 30 ? 30 : bookingWindowDays < 90 ? 90 : null;

  const COLUMN_LABEL = {
    time: t('bookings_col_time', { defaultValue: 'Time' }),
    reference: t('bookings_col_ref', { defaultValue: 'Ref' }),
    id: t('booking_id', { defaultValue: 'Booking ID' }),
    customer: t('customer', { defaultValue: 'Customer' }),
    driver: t('driver', { defaultValue: 'Driver' }),
    status: t('status', { defaultValue: 'Status' }),
    bookingType: calmCaps(t('booking_type_col', { defaultValue: 'Booking type' })),
    otp: t('bookings_col_start_code', { defaultValue: 'Start code' }),
    payment_mode: t('payment_mode_web', { defaultValue: 'Payment mode' }),
    fare: t('fare', { defaultValue: 'Fare' }),
    anomaly: t('bookings_legend_anomaly', { defaultValue: 'Anomaly' }),
    actions: t('actions', { defaultValue: 'Actions' }),
  };

  const SORTABLE = ['time', 'reference', 'customer', 'driver', 'status', 'fare'];
  const columnOrder = ['time', 'reference', 'id', 'customer', 'driver', 'status', 'bookingType', 'otp', 'payment_mode', 'fare', 'anomaly', 'actions']
    .filter((id) => id === 'time' || id === 'reference' || id === 'actions' || isVisible(id));

  const compact = density === 'compact';
  const rowHeight = compact ? 40 : 52;
  const iconBtnSize = compact ? 32 : 38;

  const markLegend = [
    { kind: 'price_above', label: t('bookings_anomaly_price_above', { defaultValue: 'Price above estimate' }) },
    { kind: 'price_below', label: t('bookings_anomaly_price_below', { defaultValue: 'Price below estimate' }) },
    { kind: 'longer', label: t('bookings_anomaly_took_longer', { defaultValue: 'Took longer' }) },
    { kind: 'quicker', label: t('bookings_anomaly_much_quicker', { defaultValue: 'Much quicker' }) },
  ];

  // ── Cells ────────────────────────────────────────────────────────────
  const stop = (e) => e.stopPropagation();

  const renderCustomer = (row) => {
    // Shared trips have N customers, not a single one — the name opens the
    // booking (which lists every customer) and the chip names them all.
    if (row.bookingType === 'shared_trip') {
      const list = Array.isArray(row.passengers) ? row.passengers : [];
      return (
        <Box onClick={stop}>
          {row.customer_name ? (
            <ClickableBookingLink value={row.customer_name} searchBy="reference" bookingId={row.id} iconType="receipt" showIcon={false} copy={false} style={{ color: c.INK, fontWeight: 600, padding: 0 }} />
          ) : null}
          {list.length > 0 ? (
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{list.map((p) => `${(p.firstName || '') + (p.lastName ? ' ' + p.lastName : '')} — ${p.phone || '-'}`).join('\n')}</span>}
            >
              <Chip
                size="small"
                label={t('shared_trip_chip', {
                  count: list.length,
                  defaultValue: 'Shared ({{count}} customers)',
                  defaultValue_one: 'Shared ({{count}} customer)',
                })}
                sx={{ mt: 0.5, bgcolor: c.AMBER_TINT, color: c.AMBER_TEXT, fontWeight: 600 }}
              />
            </Tooltip>
          ) : null}
        </Box>
      );
    }
    return (
      <Box sx={{ minWidth: 140 }}>
        {row.customer_name ? (
          <Box onClick={stop} sx={{ display: 'inline-block' }}>
            <ClickableUserLink
              value={row.customer_name}
              type="customer"
              searchField="name"
              userId={row.customer || row.customer_id}
              showIcon={false}
              style={{ color: c.INK, fontWeight: 600, padding: 0 }}
            />
          </Box>
        ) : (
          <Box sx={{ color: c.TEXT_3 }}>-</Box>
        )}
        {row.customer_contact && String(row.customer_contact).trim() ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap' }}>
            {/* Demo mode (numbers hidden) hides it and its copy button, as
                every other page does; otherwise it copies without spaces
                (+251922334455), ready for MicroSIP. */}
            {settings && settings.AllowCriticalEditsAdmin ? (
              <>
                <span>{formatPhoneDisplay(row.customer_contact)}</span>
                <CopyButton value={row.customer_contact} phone what={t('customer_contact', { defaultValue: 'Customer phone' })} sx={{ my: -0.75 }} />
              </>
            ) : (
              <span>{t('hidden_demo', { defaultValue: 'Hidden' })}</span>
            )}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderDriver = (row) => (
    <Box sx={{ minWidth: 120 }}>
      {row.driver_name ? (
        <Box onClick={stop} sx={{ display: 'inline-block' }}>
          <ClickableUserLink
            value={row.driver_name}
            type="driver"
            searchField="name"
            userId={row.driver || row.driver_id}
            showIcon={false}
            style={{ color: c.INK, fontWeight: 500, padding: 0 }}
          />
        </Box>
      ) : (
        <Box sx={{ color: c.TEXT_3 }}>{t('bookings_no_driver', { defaultValue: 'No driver yet' })}</Box>
      )}
      {row.carType ? <Box sx={{ fontSize: 12.5, color: c.TEXT_3 }}>{t(getLangKey(row.carType))}</Box> : null}
    </Box>
  );

  const renderFare = (row) => {
    const info = fareInfoOf(row);
    const amount = formatAmount(info.amount, settings.decimal);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
        <Box sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {info.kind === 'estimate' ? `~${amount}` : amount}
        </Box>
        {info.kind === 'final' ? (
          info.estimate ? (
            <Box sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {t('bookings_est_short', { amount: formatAmount(info.estimate, settings.decimal), defaultValue: 'est. {{amount}}' })}
            </Box>
          ) : null
        ) : (
          <FareTag kind={info.kind} />
        )}
      </Box>
    );
  };

  const renderAnomaly = (row) => {
    const a = analyseFare(row);
    if (!a.marks.length) return <Box sx={{ color: c.TEXT_3 }} aria-label={t('bookings_no_anomaly', { defaultValue: 'No anomaly' })}>—</Box>;
    const lines = analysisLines(a, t, settings);
    return (
      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{lines.join('\n')}</span>}>
        <Box onClick={stop} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }} tabIndex={0}>
          {a.marks.map((m) => (
            <AnomalyMark key={m.kind} mark={m} critical={a.score >= 70} label={anomalyMarkText(m, t)} />
          ))}
        </Box>
      </Tooltip>
    );
  };

  const actionButtonSx = { width: iconBtnSize, height: iconBtnSize };

  const renderActions = (row) => {
    const cancellable = isCancellable(row);
    const started = row.status === 'STARTED';
    return (
      <Box onClick={stop} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
        <Tooltip describeChild title={calmCaps(t('cancel_booking', { defaultValue: 'Cancel booking' }))}>
          <span>
            <IconButton
              size="small"
              aria-label={calmCaps(t('cancel_booking', { defaultValue: 'Cancel booking' }))}
              disabled={!cancellable}
              onClick={() => handleCancelClick(row)}
              sx={{ ...actionButtonSx, color: c.DANGER_TEXT }}
            >
              <HighlightOffRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {canForceEnd ? (
          <Tooltip describeChild title={t('bookings_force_end_trip', { defaultValue: 'Force end trip' })}>
            <span>
              <IconButton
                size="small"
                aria-label={t('bookings_force_end_trip', { defaultValue: 'Force end trip' })}
                disabled={!started}
                onClick={() => handleForceEnd(row)}
                sx={{ ...actionButtonSx, color: c.DANGER_TEXT }}
              >
                <StopCircleOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
        <Tooltip describeChild title={t('bookings_more_actions', { defaultValue: 'More actions' })}>
          <IconButton
            size="small"
            aria-label={t('bookings_more_actions', { defaultValue: 'More actions' })}
            aria-haspopup="menu"
            onClick={(e) => setMoreMenu({ anchorEl: e.currentTarget, row })}
            sx={{ ...actionButtonSx, color: c.INK_2 }}
          >
            <MoreHorizRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderCell = (id, row) => {
    switch (id) {
      case 'time':
        return (
          <>
            <Box sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatAddisTime(row.bookingDate)}</Box>
            <Box sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap' }}>{addisShortDate(row.bookingDate)}</Box>
          </>
        );
      case 'reference':
        // A link that can be selected (not a <button>, whose text cannot be
        // selected), plus a copy button: the call centre copies references.
        return row.reference ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>
            <Link
              component="span"
              role="link"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                // The press point comes from the row (the mousedown bubbles).
                if (!selectingTextIn(e.currentTarget, e, pressRef.current)) openDetails(row);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                e.stopPropagation();
                openDetails(row);
              }}
              underline="always"
              sx={{
                fontWeight: 700,
                color: c.ACCENT_TEXT,
                letterSpacing: '0.02em',
                fontSize: 14,
                cursor: 'pointer',
                userSelect: 'text',
                borderRadius: '4px',
                '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
              }}
            >
              {row.reference}
            </Link>
            <CopyButton value={row.reference} what={t('booking_page_reference', { defaultValue: 'Booking reference' })} sx={{ my: -0.5 }} />
          </Box>
        ) : '-';
      case 'id':
        return <Box sx={{ fontSize: 12.5, color: c.INK_2, wordBreak: 'break-all', maxWidth: 180 }}>{row.id}</Box>;
      case 'customer':
        return renderCustomer(row);
      case 'driver':
        return renderDriver(row);
      case 'status':
        return <BookingStatusChip status={row.status} />;
      case 'bookingType':
        return (
          <Chip
            size="small"
            label={getBookingTypeLabel(row, t)}
            sx={{ bgcolor: c.SURFACE_ALT, color: c.INK_2, fontWeight: 600 }}
          />
        );
      case 'otp':
        return row.otp ? <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, letterSpacing: '0.08em' }}>{row.otp}</Box> : <Box sx={{ color: c.TEXT_3 }}>—</Box>;
      case 'payment_mode':
        return paymentModeLabel(row.payment_mode);
      case 'fare':
        return renderFare(row);
      case 'anomaly':
        return renderAnomaly(row);
      case 'actions':
        return renderActions(row);
      default:
        return null;
    }
  };

  // ── Dialog helpers ───────────────────────────────────────────────────
  const moreRow = moreMenu.row;
  const editRow = editDialog.row;

  const bulkHint = t('bulk_convert_rule', {
    defaultValue: 'Only pending bookings can be converted, the same rule as editing one booking. Payment mode and status are two separate conversions; run each on its own.',
  });

  return (
    <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Find bookings: the search box always shows (with an X to clear it);
          "Hide filters" folds the time range, status and type away. They
          still apply, so the ones that narrow the list show as chips. */}
      <Paper component="section" aria-label={t('bookings_find', { defaultValue: 'Find bookings' })} sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <SearchField
          value={search}
          onChange={(text) => { setSearch(text); setCurrentPage(0); }}
          placeholder={t('bookings_search_placeholder', { defaultValue: 'Search reference, customer or phone' })}
          width="auto"
          sx={{
            flex: '1 1 260px',
            minWidth: 200,
            // As tall as the controls beside it: 40 px, 44 on a touch screen.
            '& .MuiOutlinedInput-root': { minHeight: 40, '@media (pointer: coarse)': { minHeight: 44 } },
            '& .MuiOutlinedInput-input': { py: '8.5px' },
          }}
        />
        <Box id="bookings-filters" sx={{ display: filtersOpen ? 'contents' : 'none' }}>
          <TextField
            select
            size="small"
            value={bookingWindowDays}
            onChange={(e) => handleBookingWindowChange(e.target.value)}
            inputProps={{ 'aria-label': calmCaps(t('time_range', { defaultValue: 'Time range' })) }}
            InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonthOutlinedIcon sx={{ color: c.INK_2, fontSize: 20 }} /></InputAdornment> }}
            // 44 px on a touch screen, like the search box and buttons beside it.
            sx={{ flex: '0 0 auto', minWidth: 170, '& .MuiOutlinedInput-root': { '@media (pointer: coarse)': { minHeight: 44 } } }}
          >
            {[7, 30, 90].map((days) => (
              <MenuItem key={days} value={days}>{windowLabel(days)}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            color="neutral"
            onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
            endIcon={<ArrowDropDownRoundedIcon />}
            aria-haspopup="menu"
            sx={{ flex: '0 0 auto' }}
          >
            <Box component="span" sx={{ color: c.INK_2, fontWeight: 500, mr: 0.75 }}>{t('status', { defaultValue: 'Status' })}</Box>
            {t('bookings_filter_count', { shown: statusShown, total: statusTotal, defaultValue: '{{shown}} of {{total}}' })}
          </Button>
          <Button
            variant="outlined"
            color="neutral"
            onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
            endIcon={<ArrowDropDownRoundedIcon />}
            aria-haspopup="menu"
            sx={{ flex: '0 0 auto' }}
          >
            <Box component="span" sx={{ color: c.INK_2, fontWeight: 500, mr: 0.75 }}>{t('type', { defaultValue: 'Type' })}</Box>
            {typeShown === BOOKING_TYPE_ORDER.length
              ? t('bookings_filter_all', { count: typeShown, defaultValue: 'All {{count}}' })
              : t('bookings_filter_count', { shown: typeShown, total: BOOKING_TYPE_ORDER.length, defaultValue: '{{shown}} of {{total}}' })}
          </Button>
        </Box>
        {!filtersOpen && hiddenFilterTokens.length ? (
          <ActiveFilters tokens={hiddenFilterTokens} onRemove={removeHiddenFilter} sx={{ flex: '0 1 auto', minWidth: 0 }} />
        ) : null}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, ml: { md: 'auto' } }}>
          {canBulkConvert ? (
            <Button
              variant="outlined"
              color="neutral"
              startIcon={<SwapHorizRoundedIcon sx={{ color: c.ACCENT }} />}
              onClick={() => setBulkOpen(true)}
              sx={{ flex: '0 0 auto' }}
            >
              {t('bulk_convert', { defaultValue: 'Bulk convert' })}
            </Button>
          ) : null}
          <FilterToggleButton
            open={filtersOpen}
            onClick={toggleFilters}
            activeCount={hiddenFilterTokens.length}
            controls="bookings-filters"
            sx={{ flex: '0 0 auto', minHeight: 40 }}
          />
        </Box>
      </Paper>

      {/* What the marks mean (Q19: meaning on the page, not in a tooltip) */}
      <Box role="note" aria-label={t('bookings_legend_title', { defaultValue: 'What the marks mean' })} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 3, rowGap: 1, px: 0.5 }}>
        {/* Two groups that wrap as wholes, so a narrow screen never leaves a lone chip on a line */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.INK_2 }}>
            {t('bookings_legend_anomaly', { defaultValue: 'Anomaly' })}
            {anomalyCount > 0 ? ` · ${t('bookings_legend_anomaly_count', { count: anomalyCount, defaultValue: '{{count}} in view' })}` : ''}
          </Typography>
          {markLegend.map((m) => (
            <AnomalyMark
              key={m.kind}
              mark={{ kind: m.kind, value: 1 }}
              label={anomalyCounts[m.kind] > 0 ? `${m.label} (${anomalyCounts[m.kind]})` : m.label}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.INK_2 }}>{t('fare', { defaultValue: 'Fare' })}</Typography>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: 1, height: 26, borderRadius: '13px', bgcolor: c.INFO_TINT }}>
            <FareTag kind="fixed" />
          </Box>
          <FareTag kind="estimate" />
          <Typography component="span" sx={{ fontSize: 13, color: c.TEXT_3 }}>
            {t('bookings_legend_est_note', { defaultValue: 'est. = the system estimate' })}
          </Typography>
        </Box>
      </Box>

      {/* The table */}
      <Paper component="section" aria-label={t('nav_bookings', { defaultValue: 'Bookings' })} sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: `1px solid ${c.OUTLINE}` }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mr: 'auto', minWidth: 0 }}>
            <Typography component="h1" sx={{ fontSize: 17, fontWeight: 700, color: c.INK }}>{headerCount}</Typography>
            <Typography component="span" sx={{ fontSize: 13.5, color: c.TEXT_3 }}>
              {[headerWindow, statusFilter.CANCELLED ? null : t('bookings_cancelled_hidden', { defaultValue: 'cancelled hidden' })].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.INK_2, display: { xs: 'none', sm: 'inline' } }}>
              {calmCaps(t('rows', { defaultValue: 'Rows' }))}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={density}
              onChange={handleDensity}
              aria-label={t('bookings_density', { defaultValue: 'Row density' })}
            >
              <ToggleButton value="comfortable" sx={{ px: 1.25, gap: 0.75, textTransform: 'none', fontWeight: 600 }}>
                <TableRowsOutlinedIcon fontSize="small" />
                {t('bookings_density_comfortable', { defaultValue: 'Comfortable' })}
              </ToggleButton>
              <ToggleButton value="compact" sx={{ px: 1.25, gap: 0.75, textTransform: 'none', fontWeight: 600 }}>
                <DensitySmallRoundedIcon fontSize="small" />
                {t('bookings_density_compact', { defaultValue: 'Compact' })}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Button
            variant="outlined"
            color="neutral"
            startIcon={<ViewColumnOutlinedIcon />}
            onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
            aria-haspopup="menu"
          >
            {t('bookings_columns', { defaultValue: 'Columns' })}
            {hiddenCount > 0 ? (
              <Box component="span" sx={{ ml: 0.75, color: c.TEXT_3, fontWeight: 500 }}>
                {t('bookings_columns_hidden', { count: hiddenCount, defaultValue: '{{count}} hidden' })}
              </Box>
            ) : null}
          </Button>
          {isAdmin ? (
            <Button
              variant="outlined"
              color="neutral"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              aria-haspopup="menu"
              disabled={sortedRows.length === 0}
            >
              {calmCaps(t('export', { defaultValue: 'Export' }))}
            </Button>
          ) : null}
        </Box>
        {refreshing || bulkProcessing ? <LinearProgress aria-label={t('loading', { defaultValue: 'Loading…' })} sx={{ height: 3 }} /> : null}

        {!loadingFirst && sortedRows.length === 0 ? (
          (data && data.length > 0) || activeFilterCount > 0 ? (
            <Box sx={{ pb: 3 }}>
              <EmptyState
                icon={<FilterAltOffOutlinedIcon />}
                title={t('bookings_empty_filtered', {
                  window: headerWindow,
                  defaultValue: 'No bookings in the {{window}} match your filters.',
                })}
                body={search.trim() ? t('bookings_empty_search', { query: search.trim(), defaultValue: 'Nothing matches “{{query}}”.' }) : undefined}
                actionLabel={t('bookings_clear_filters', { defaultValue: 'Clear filters' })}
                onAction={clearFilters}
              />
              {nextWindow ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: -2 }}>
                  <Button variant="text" onClick={() => handleBookingWindowChange(nextWindow)}>
                    {t('bookings_show_window', { count: nextWindow, defaultValue: 'Show the last {{count}} days' })}
                  </Button>
                </Box>
              ) : null}
            </Box>
          ) : (
            <EmptyState
              icon={<EventBusyOutlinedIcon />}
              title={t('bookings_empty_window', { window: headerWindow, defaultValue: 'No bookings in the {{window}}.' })}
              actionLabel={nextWindow ? t('bookings_show_window', { count: nextWindow, defaultValue: 'Show the last {{count}} days' }) : undefined}
              onAction={nextWindow ? () => handleBookingWindowChange(nextWindow) : undefined}
            />
          )
        ) : (
          <>
            {/* Q18: the table scrolls inside the page on a computer, sized
                with 100dvh so the browser's own bars never hide the last
                rows; on a phone the page scrolls instead. */}
            <TableContainer
              sx={{
                [theme.breakpoints.up('md')]: {
                  maxHeight: 'calc(100vh - 340px)',
                  minHeight: 320,
                  '@supports (height: 100dvh)': { maxHeight: 'calc(100dvh - 340px)' },
                },
              }}
            >
              <Table
                stickyHeader
                size={compact ? 'small' : 'medium'}
                aria-label={t('nav_bookings', { defaultValue: 'Bookings' })}
                sx={{
                  minWidth: 860,
                  '& .MuiTableCell-root': { px: 1.25, py: compact ? 0.5 : 1, fontSize: 14, lineHeight: 1.45, verticalAlign: 'middle' },
                  '& .MuiTableCell-head': {
                    bgcolor: ZEBRA,
                    color: c.INK_2,
                    fontSize: 12.5,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    py: 1.25,
                  },
                  '& tbody tr:nth-of-type(even) > td': { bgcolor: ZEBRA },
                  '& tbody tr.MuiTableRow-hover:hover > td': { bgcolor: c.ACCENT_TINT },
                }}
              >
                <TableHead>
                  <TableRow>
                    {columnOrder.map((id) => {
                      const sortable = SORTABLE.indexOf(id) >= 0;
                      const active = sort.by === id;
                      const align = id === 'fare' || id === 'actions' ? 'right' : 'left';
                      return (
                        <TableCell
                          key={id}
                          align={align}
                          sortDirection={active ? sort.dir : false}
                          sx={id === 'actions' ? { width: canForceEnd ? 136 : 96 } : undefined}
                        >
                          {id === 'actions' ? (
                            <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                              {COLUMN_LABEL.actions}
                            </Box>
                          ) : sortable ? (
                            <TableSortLabel active={active} direction={active ? sort.dir : 'asc'} onClick={() => handleSort(id)}>
                              {COLUMN_LABEL[id]}
                            </TableSortLabel>
                          ) : (
                            COLUMN_LABEL[id]
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingFirst ? (
                    <TableSkeletonRows rows={6} columns={columnOrder.length} rowHeight={rowHeight} />
                  ) : (
                    pageRows.map((row) => (
                      <TableRow
                        hover
                        key={row.id}
                        onMouseDown={notePress}
                        onClick={(e) => handleRowClick(e, row)}
                        sx={{ cursor: 'pointer', height: rowHeight }}
                      >
                        {columnOrder.map((id) => (
                          <TableCell key={id} align={id === 'fare' || id === 'actions' ? 'right' : 'left'}>
                            {renderCell(id, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sortedRows.length}
              page={page}
              onPageChange={(e, p) => setCurrentPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setRowsPerPage(v);
                writePref('rpp', uid, v);
                setCurrentPage(0);
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage={t('rows_per_page', { defaultValue: 'Rows per page' })}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('of', { defaultValue: 'of' })} ${count}`}
              sx={{ borderTop: `1px solid ${c.OUTLINE}` }}
            />
          </>
        )}
      </Paper>

      {/* Status filter */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)}>
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, pb: 1 }}>
          <Button size="small" variant="text" onClick={handleSelectAll}>{calmCaps(t('select_all', { defaultValue: 'Select all' }))}</Button>
          <Button size="small" variant="text" color="neutral" onClick={handleDeselectAll}>{calmCaps(t('deselect_all', { defaultValue: 'Deselect all' }))}</Button>
        </Box>
        <Divider />
        {Object.keys(statusFilter).map((status) => (
          <MenuItem key={status} onClick={() => handleStatusFilterChange(status)} dense>
            <Checkbox size="small" checked={statusFilter[status]} tabIndex={-1} disableRipple sx={{ p: 0.5, mr: 1 }} />
            <BookingStatusChip status={status} />
          </MenuItem>
        ))}
      </Menu>

      {/* Booking type filter */}
      <Menu anchorEl={typeMenuAnchor} open={Boolean(typeMenuAnchor)} onClose={() => setTypeMenuAnchor(null)}>
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, pb: 1 }}>
          <Button size="small" variant="text" onClick={handleSelectAllBookingTypes}>{calmCaps(t('select_all', { defaultValue: 'Select all' }))}</Button>
          <Button size="small" variant="text" color="neutral" onClick={handleDeselectAllBookingTypes}>{calmCaps(t('deselect_all', { defaultValue: 'Deselect all' }))}</Button>
        </Box>
        <Divider />
        {BOOKING_TYPE_ORDER.map((typeKey) => (
          <MenuItem key={typeKey} onClick={() => handleBookingTypeFilterChange(typeKey)} dense>
            <Checkbox size="small" checked={!!bookingTypeFilter[typeKey]} tabIndex={-1} disableRipple sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={bookingTypeLabelFor(typeKey, t)} />
          </MenuItem>
        ))}
      </Menu>

      {/* Columns */}
      <Menu anchorEl={columnsMenuAnchor} open={Boolean(columnsMenuAnchor)} onClose={() => setColumnsMenuAnchor(null)}>
        {hideableColumns.map((id) => (
          <MenuItem key={id} onClick={() => toggleColumn(id)} dense>
            <Checkbox size="small" checked={isVisible(id)} tabIndex={-1} disableRipple sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={COLUMN_LABEL[id]} />
          </MenuItem>
        ))}
      </Menu>

      {/* Export (admin) */}
      <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
        {canExportCsv ? (
          <MenuItem onClick={handleExportCsv}>
            <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('bookings_export_csv', { defaultValue: 'Download CSV' })} />
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleExportPdf}>
          <ListItemIcon><PrintOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('bookings_export_pdf', { defaultValue: 'Print or save as PDF' })} />
        </MenuItem>
      </Menu>

      {/* More actions for one booking */}
      <Menu anchorEl={moreMenu.anchorEl} open={Boolean(moreMenu.anchorEl)} onClose={closeMoreMenu}>
        {moreRow ? (
          <MenuItem onClick={() => { closeMoreMenu(); openDetails(moreRow); }}>
            <ListItemIcon><ReceiptLongOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={calmCaps(t('booking_details', { defaultValue: 'Booking details' }))} />
          </MenuItem>
        ) : null}
        {moreRow && canEditBooking(moreRow) ? (
          <MenuItem onClick={() => openEditDialog('status', moreRow)}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('bookings_change_status', { defaultValue: 'Change status' })} />
          </MenuItem>
        ) : null}
        {moreRow && canEditBooking(moreRow) ? (
          <MenuItem onClick={() => openEditDialog('payment', moreRow)}>
            <ListItemIcon><PaymentsOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('bookings_change_payment', { defaultValue: 'Change payment mode' })} />
          </MenuItem>
        ) : null}
        {moreRow && canAssign(moreRow) ? (
          <MenuItem onClick={() => { closeMoreMenu(); setAssignBooking(moreRow); setUserCombo(null); setOpen(true); }}>
            <ListItemIcon><PersonAddAlt1OutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={calmCaps(t('assign_driver', { defaultValue: 'Assign driver' }))} />
          </MenuItem>
        ) : null}
        {moreRow && canSelectBid(moreRow) ? (
          <MenuItem onClick={() => { closeMoreMenu(); setSelectedBooking(moreRow); setBidModalStatus(true); }}>
            <ListItemIcon><PlaylistAddCheckRoundedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={calmCaps(t('selectBid', { defaultValue: 'Select bid' }))} />
          </MenuItem>
        ) : null}
        {moreRow && canPayNow(moreRow) ? (
          <MenuItem onClick={() => { closeMoreMenu(); processPayment(moreRow); }}>
            <ListItemIcon><PaymentsOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={calmCaps(t('paynow_button', { defaultValue: 'Pay now' }))} />
          </MenuItem>
        ) : null}
      </Menu>

      {/* Change status / payment mode of one pending booking */}
      <Dialog open={editDialog.open} onClose={closeEditDialog} fullScreen={phone} maxWidth="xs" fullWidth aria-labelledby="bk-edit-title">
        <DialogTitle id="bk-edit-title">
          {editDialog.kind === 'status'
            ? t('bookings_change_status_title', { reference: (editRow && editRow.reference) || '', defaultValue: 'Change the status of {{reference}}' })
            : t('bookings_change_payment_title', { reference: (editRow && editRow.reference) || '', defaultValue: 'Change the payment mode of {{reference}}' })}
        </DialogTitle>
        <DialogContent>
          {editRow ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">{t('bookings_now', { defaultValue: 'Now' })}</Typography>
              {editDialog.kind === 'status' ? <BookingStatusChip status={editRow.status} /> : <Chip size="small" label={paymentModeLabel(editRow.payment_mode)} sx={{ fontWeight: 600 }} />}
            </Box>
          ) : null}
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={editDialog.value} onChange={(e) => setEditDialog((prev) => ({ ...prev, value: e.target.value }))}>
              {(editDialog.kind === 'status' ? ROW_STATUS_TARGETS : BULK_PAYMENT_MODES).map((v) => (
                <FormControlLabel
                  key={v}
                  value={v}
                  control={<Radio />}
                  label={editDialog.kind === 'status' ? bookingStatusLabel(v, t) : paymentModeLabel(v)}
                  sx={{ minHeight: 44 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {editDialog.kind === 'status' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('bulk_status_settles', { defaultValue: 'Reached and Complete also settle the payment fields.' })}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={closeEditDialog}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
          <Button
            variant="contained"
            onClick={saveEditDialog}
            disabled={!editDialog.value || (editDialog.kind === 'payment' && editRow && editDialog.value === (editRow.payment_mode || 'cash'))}
          >
            {editDialog.kind === 'status'
              ? t('bookings_change_status', { defaultValue: 'Change status' })
              : t('bookings_change_payment', { defaultValue: 'Change payment mode' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk convert (admin / call-centre supervisor) */}
      {canBulkConvert ? (
        <Dialog open={bulkOpen} onClose={() => (bulkProcessing ? null : setBulkOpen(false))} fullScreen={phone} maxWidth="md" fullWidth aria-labelledby="bk-bulk-title">
          <DialogTitle id="bk-bulk-title">{t('bulk_convert', { defaultValue: 'Bulk convert' })}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('bulk_convert_hint', { defaultValue: 'Convert the payment mode or the status of every loaded booking that qualifies.' })}
              {' '}
              {bulkHint}
              {' '}
              {t('bookings_loaded_count', { count: filteredData.length, defaultValue: '{{count}} bookings loaded.' })}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {/* Payment-mode conversion */}
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
                  {t('convert_payment_mode', { defaultValue: 'Convert payment mode' })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    select
                    size="small"
                    label={t('from', { defaultValue: 'From' })}
                    value={bulkPmFrom}
                    onChange={(e) => setBulkPmFrom(e.target.value)}
                    sx={{ minWidth: 140 }}
                  >
                    {BULK_PAYMENT_MODES.map(m => (
                      <MenuItem key={m} value={m}>{paymentModeLabel(m)}</MenuItem>
                    ))}
                  </TextField>
                  <SwapHorizRoundedIcon sx={{ color: c.TEXT_3 }} aria-hidden="true" />
                  <TextField
                    select
                    size="small"
                    label={t('to', { defaultValue: 'To' })}
                    value={bulkPmTo}
                    onChange={(e) => setBulkPmTo(e.target.value)}
                    sx={{ minWidth: 140 }}
                  >
                    {BULK_PAYMENT_MODES.map(m => (
                      <MenuItem key={m} value={m}>{paymentModeLabel(m)}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('bulk_pm_matches', { count: bulkPaymentMatches.length, mode: paymentModeLabel(bulkPmFrom), defaultValue: '{{count}} pending bookings use {{mode}}.' })}
                </Typography>
                <Box>
                  <Button
                    variant="contained"
                    disabled={bulkProcessing || bulkPaymentMatches.length === 0 || bulkPmFrom === bulkPmTo}
                    onClick={requestBulkPaymentConvert}
                    startIcon={<SwapHorizRoundedIcon />}
                  >
                    {bulkPaymentMatches.length > 0
                      ? t('bulk_convert_button', { count: bulkPaymentMatches.length, defaultValue: 'Convert ({{count}})' })
                      : t('convert', { defaultValue: 'Convert' })}
                  </Button>
                </Box>
              </Paper>

              {/* Booking-status conversion */}
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
                  {t('convert_booking_status', { defaultValue: 'Convert booking status' })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {/* Original status is locked to PENDING — only PENDING
                      bookings are editable (per-row "PENDING-only" rule). */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" component="div">{t('from', { defaultValue: 'From' })}</Typography>
                    <BookingStatusChip status={BULK_FROM_STATUS} />
                  </Box>
                  <SwapHorizRoundedIcon sx={{ color: c.TEXT_3 }} aria-hidden="true" />
                  <TextField
                    select
                    size="small"
                    label={t('to', { defaultValue: 'To' })}
                    value={bulkStatusTo}
                    onChange={(e) => setBulkStatusTo(e.target.value)}
                    sx={{ minWidth: 160 }}
                  >
                    {BULK_STATUS_TO.map(s => (
                      <MenuItem key={s} value={s}>{bookingStatusLabel(s, t)}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('bulk_status_matches', { count: bulkStatusMatches.length, defaultValue: '{{count}} pending cash bookings can be converted (cash only).' })}
                  {(bulkStatusTo === 'COMPLETE' || bulkStatusTo === 'REACHED')
                    ? ` ${t('bulk_status_settles', { defaultValue: 'Reached and Complete also settle the payment fields.' })}`
                    : ''}
                </Typography>
                <Box>
                  <Button
                    variant="contained"
                    disabled={bulkProcessing || bulkStatusMatches.length === 0}
                    onClick={requestBulkStatusConvert}
                    startIcon={<SwapHorizRoundedIcon />}
                  >
                    {bulkStatusMatches.length > 0
                      ? t('bulk_convert_button', { count: bulkStatusMatches.length, defaultValue: 'Convert ({{count}})' })
                      : t('convert', { defaultValue: 'Convert' })}
                  </Button>
                </Box>
              </Paper>
            </Box>

            {bulkProcessing && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={bulkProgress.total ? Math.round((bulkProgress.done / bulkProgress.total) * 100) : 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('bulk_progress', { done: bulkProgress.done, total: bulkProgress.total, defaultValue: 'Converting {{done}} of {{total}}…' })}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="text" color="neutral" onClick={() => setBulkOpen(false)} disabled={bulkProcessing}>
              {calmCaps(t('close', { defaultValue: 'Close' }))}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      <BidModal
        ref={rootRef.current}
        role={role}
        selectedBooking={selectedBooking}
        bidModalStatus={bidModalStatus}
        handleBidModalClose={handleBidModalClose}
        classes={{}}
        settings={settings}
        acceptBid={() => {
          const bookingObj = acceptBid(selectedBooking, selectedBidder);
          processPayment(bookingObj);
          handleBidModalClose();
        }}
        selectedBidder={selectedBidder}
        handleChange={handleChange}
      />

      {/* Customer: pay a pending booking from the wallet */}
      {selectedBooking &&
      selectedBooking.payment_mode === "wallet" &&
      (selectedBooking.status === "PENDING" ||
        (selectedBooking &&
          selectedBooking.selectedBid &&
          selectedBooking.status === "NEW")) &&
      role === "customer" ? (
        <Dialog open={walletModalStatus} onClose={handleWalletModalClose} fullScreen={phone} maxWidth="xs" fullWidth aria-labelledby="bk-wallet-title">
          <DialogTitle id="bk-wallet-title">{t('payment', { defaultValue: 'Payment' })}</DialogTitle>
          <DialogContent>
            {selectedBooking.estimate ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {`${t("system_estimate", { defaultValue: "System estimate" })}: ${formatMoney(selectedBooking.estimate, settings)}`}
              </Typography>
            ) : null}
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{analysisLines(analyseFare(selectedBooking), t, settings).join('\n')}</span>}>
              <Typography component="p" variant="numeral" tabIndex={0} sx={{ fontSize: 30, display: 'inline-block' }}>
                {formatMoney(selectedBooking.trip_cost, settings)}
              </Typography>
            </Tooltip>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t("use_wallet_balance") +
                " " +
                (settings.swipe_symbol === false
                  ? settings.symbol + auth.profile.walletBalance
                  : auth.profile.walletBalance + settings.symbol) +
                ")"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="text" color="neutral" onClick={handleWalletModalClose}>
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button variant="contained" onClick={handleWalletPayment}>
              {calmCaps(t("paynow_button", { defaultValue: "Pay now" }))}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/* Customer: pay a pending booking by card through the chosen provider */}
      {selectedBooking &&
      selectedBooking.paymentPacket &&
      selectedBooking.payment_mode === "card" &&
      ((selectedBooking.status === "PENDING" || selectedBooking.status === "PAYMENT_PENDING") ||
        (selectedBooking &&
          selectedBooking.selectedBid &&
          selectedBooking.status === "NEW")) &&
      role === "customer" ? (
        <Dialog open={paymentModalStatus} onClose={handlePaymentModalClose} fullScreen={phone} maxWidth="xs" fullWidth aria-labelledby="bk-card-title">
          {providers && selectedProvider && selectedBooking ? (
            <form action={selectedProvider.link} method="POST">
              <input type="hidden" name="order_id" value={selectedBooking.id} />
              <input
                type="hidden"
                name="amount"
                value={
                  selectedBooking.selectedBid
                    ? selectedBooking.selectedBid.trip_cost
                    : selectedBooking.trip_cost
                }
              />
              <input type="hidden" name="currency" value={settings.code} />
              <input type="hidden" name="product_name" value={t('bookingPayment', { defaultValue: 'Booking payment' })} />
              <input type="hidden" name="first_name" value={auth.profile.firstName} />
              <input type="hidden" name="last_name" value={auth.profile.lastName} />
              <input type="hidden" name="quantity" value={1} />
              <input type="hidden" name="cust_id" value={selectedBooking.customer} />
              <input type="hidden" name="mobile_no" value={selectedBooking.customer_contact} />
              <input type="hidden" name="email" value={selectedBooking.customer_email} />
              <DialogTitle id="bk-card-title">{t('payment', { defaultValue: 'Payment' })}</DialogTitle>
              <DialogContent>
                <TextField
                  select
                  fullWidth
                  id="selectedProviderIndex"
                  name="selectedProviderIndex"
                  value={selectedProviderIndex}
                  label={t("bookings_payment_provider", { defaultValue: "Pay with" })}
                  onChange={handleChange}
                  sx={{ mt: 1 }}
                >
                  {providers.map((provider, index) => (
                    <MenuItem key={provider.name} value={index}>
                      <img style={{ height: 24, margin: 7 }} src={icons[provider.name]} alt={provider.name} />
                    </MenuItem>
                  ))}
                </TextField>
              </DialogContent>
              <DialogActions>
                <Button variant="text" color="neutral" onClick={handlePaymentModalClose}>
                  {t('cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button variant="contained" type="submit" onClick={handlePaymentModalClose}>
                  {calmCaps(t("paynow_button", { defaultValue: "Pay now" }))}
                </Button>
              </DialogActions>
            </form>
          ) : null}
        </Dialog>
      ) : null}

      <ConfirmationDialogRaw
        open={openConfirm}
        onClose={onConfirmClose}
        value={""}
      />

      {/* Admin / fleet admin: assign a driver to a new booking */}
      {users && assignBooking ? (
        <Dialog open={open} onClose={handleClose} fullScreen={phone} maxWidth="sm" fullWidth aria-labelledby="bk-assign-title">
          <DialogTitle id="bk-assign-title">
            {calmCaps(t("select_driver", { defaultValue: "Select driver" }))}
            {assignBooking.reference ? ` · ${assignBooking.reference}` : ''}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <UsersCombo
                placeholder={t('select_user', { defaultValue: 'Select a driver' })}
                users={users.filter((usr) => usr.carType === assignBooking.carType)}
                value={userCombo}
                onChange={(event, newValue) => {
                  setUserCombo(newValue);
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="text" color="neutral" onClick={handleClose}>
              {t('cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button variant="contained" onClick={assignDriver} disabled={!userCombo}>
              {calmCaps(t("assign", { defaultValue: "Assign" }))}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
};

export default BookingHistory;
